import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { onAuthStateChanged } from 'firebase/auth'
import { auth } from '../firebase'
import {
  adminGetProvider,
  adminUpdateProvider,
  adminPresignProviderUpload,
  adminDoctorUpdateProvider,
  adminDoctorPresignUpload,
  adminAlliedUpdateProvider,
  adminAlliedPresignUpload,
  adminEducationUpdateProvider,
  adminEducationPresignUpload,
  adminMigrationUpdateProvider,
  adminMigrationPresignUpload,
  adminTechUpdateProvider,
  adminTechPresignUpload,
  adminFinancialUpdateProvider,
  adminFinancialPresignUpload,
  adminPropertyUpdateProvider,
  adminPropertyPresignUpload,
  adminLegalUpdateProvider,
  adminLegalPresignUpload,
  adminGetProviderByProfession,
  adminApproveProvider,
  adminUnapproveProvider,
  adminSetProviderDisplay,
  adminProvisionProvider,
  type ProviderProfession,
  adminListUsers,
  adminListEmailTemplates,
  adminSaveEmailTemplate,
  adminPreviewEmailTemplate,
  adminListStackEmailTemplates,
  adminSaveStackEmailTemplate,
  adminPreviewStackEmailTemplate,
  type AdminEmailTemplateItem,
  type AdminEmailTemplateVersionItem,
  adminListProviderReviews,
  adminCreateProviderReview,
  adminUpdateProviderReview,
  adminDeleteProviderReview,
  adminListRequestsByProfession,
  adminListProviderStats,
} from '../api'
import NotesRichEditor from '../components/NotesRichEditor'
import Spinner from '../components/Spinner'
import RegisterEducation from './register/RegisterEducation'
import RegisterMigration from './register/RegisterMigration'
import RegisterTech from './register/RegisterTech'
import RegisterFinancial from './register/RegisterFinancial'
import RegisterProperty from './register/RegisterProperty'
import RegisterLegal from './register/RegisterLegal'
import RegisterDoctor from './register/RegisterDoctor'
import RegisterAllied from './register/RegisterAllied'

const parseList = (raw: string) =>
  raw
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean)

type CsvReviewRow = {
  rowNumber: number
  rating?: number
  reviewerName?: string
  reviewDateIso?: string
  source?: string
  comment?: string
  reviewerId?: string
  selectionId?: string
  patientId?: string
  errors?: string[]
}

type AdminRequestItem = {
  selectionId?: string
  createdAt?: string
  updatedAt?: string
  specialty?: string
  reason?: string
  patientId?: string
  stackId?: ProviderProfession | null
  stackLabel?: string | null
  client?: { name?: string | null; email?: string | null; phone?: string | null } | null
  followupRound1Response?: string | null
  followupRound1At?: string | null
  followupRound2Response?: string | null
  followupRound2At?: string | null
  followupLastResponse?: string | null
  followupLastAt?: string | null
  followupRoleLabel?: string | null
  requestedProfessionals?: Array<{
    specialistId?: string | null
    name?: string | null
    email?: string | null
    status?: string | null
  }> | null
}

type AdminUserListItem = {
  userId?: string
  email?: string
  name?: string
  scope?: string
  scopeLabel?: string
  stackId?: string
  status?: string
  createdAt?: string
}

type AdminProviderStatsRow = {
  userId?: string
  name?: string
  email?: string
  role?: string
  status?: string
  stack?: string
  lastLoginAt?: string | null
  lastAvailabilityAt?: string | null
  lastAcceptanceAt?: string | null
  lastMeetingSentAt?: string | null
  lastReviewAt?: string | null
  lastReviewReminderAt?: string | null
  lastNoteSavedAt?: string | null
  lastNotePublishedAt?: string | null
  totals?: Record<string, number>
  window30?: Record<string, number>
  window90?: Record<string, number>
  google?: { connected?: boolean; lastSyncAt?: string | null }
  calendly?: { connected?: boolean; lastSyncAt?: string | null }
}

const normalizeCsvHeader = (value: string) => value.trim().toLowerCase().replace(/[\s_-]+/g, '')
const toSafeText = (value: any, fallback = '') => {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return fallback
}
const formatCsvCell = (value: string | number | null | undefined) => {
  if (value == null) return ''
  const text = String(value)
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`
  }
  return text
}

const withTimeout = async <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | null = null
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`${label} timed out`)), ms)
      }),
    ])
  } finally {
    if (timer) clearTimeout(timer)
  }
}

const APPROVAL_RESULT_FIELDS: Array<keyof {
  userId?: string
  status?: string
  name?: string
  email?: string
  updatedAt?: string
  displayProfession?: string
  note?: string
  error?: string
}> = ['userId', 'status', 'name', 'email', 'updatedAt', 'displayProfession', 'note', 'error']
const downloadCsv = (filename: string, rows: string[]) => {
  const blob = new Blob([rows.join('\r\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

const csvHeaderMap: Record<string, keyof CsvReviewRow> = {
  rating: 'rating',
  reviewername: 'reviewerName',
  reviewer: 'reviewerName',
  name: 'reviewerName',
  reviewdateiso: 'reviewDateIso',
  reviewdate: 'reviewDateIso',
  date: 'reviewDateIso',
  datetime: 'reviewDateIso',
  slotiso: 'reviewDateIso',
  source: 'source',
  comment: 'comment',
  reviewtext: 'comment',
  text: 'comment',
  reviewerid: 'reviewerId',
  selectionid: 'selectionId',
  patientid: 'patientId',
  clientid: 'patientId',
}

const parseCsvTable = (raw: string) => {
  const text = String(raw || '').replace(/^\uFEFF/, '')
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let inQuotes = false
  const pushField = () => {
    row.push(field)
    field = ''
  }
  const pushRow = () => {
    if (!row.length && !field) return
    pushField()
    rows.push(row)
    row = []
  }
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i]
    if (char === '"') {
      if (inQuotes && text[i + 1] === '"') {
        field += '"'
        i += 1
      } else {
        inQuotes = !inQuotes
      }
      continue
    }
    if (char === ',' && !inQuotes) {
      pushField()
      continue
    }
    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && text[i + 1] === '\n') i += 1
      pushRow()
      continue
    }
    field += char
  }
  if (field || row.length) pushRow()
  return rows.filter((values) => values.some((value) => String(value || '').trim()))
}

const CONTACT_SLOT = 'REQUEST_BOOKING'
const APPROVALS_BUILD = 'approvals-20260407-10'
const APPROVAL_MESSAGE_DATA_MARKER = '||APPROVAL_DATA||'

const APPROVAL_STACKS: Array<{ id: ProviderProfession; label: string }> = [
  { id: 'education', label: 'Education' },
  { id: 'doctor', label: 'Doctor' },
  { id: 'allied', label: 'Allied Health' },
  { id: 'migration', label: 'Migration & Visa' },
  { id: 'tech', label: 'Tech, Creative & Media' },
  { id: 'financial', label: 'Financial' },
  { id: 'property', label: 'Property' },
  { id: 'legal', label: 'Legal, Governance & Compliance' },
]
const EMAIL_STACK_IDS: ProviderProfession[] = APPROVAL_STACKS.map((stack) => stack.id)
type EmailTemplateScope = ProviderProfession | 'app'
const EMAIL_TEMPLATE_SCOPES: Array<{ id: EmailTemplateScope; label: string }> = [
  ...APPROVAL_STACKS,
  { id: 'app', label: 'App (shared onboarding)' },
]

const SHARED_EMAIL_TEMPLATE_KEYS: string[] = [
  'clientWelcome',
  'providerRegistrationPending',
  'opsProviderRegistration',
  'opsProviderPhotoPending',
  'providerApproved',
  'clientRequestConfirmation',
  'guestRequestReceived',
  'selectionRequestAlert',
  'appointmentConfirmedClient',
  'appointmentConfirmedProvider',
  'appointmentRescheduledClient',
  'appointmentRescheduledProvider',
  'appointmentCancelledClient',
  'appointmentCancelledProvider',
  'meetingLinkClient',
  'meetingLinkProvider',
  'followupCheck',
  'reviewReminder',
]

const SHARED_EMAIL_TEMPLATE_SET = new Set(SHARED_EMAIL_TEMPLATE_KEYS)
const REQUEST_EXCLUDED_EMAILS = new Set([
  'contact@eucamd.com',
  'contact@eucamatch.com',
  'vu.lily.chi@gmail.com',
  'aivu3168@gmail.com',
  'woodeastzhao@gmail.com',
  'lily@eucamatch.com',
])
const REQUEST_EXCLUDED_CLIENT_NAMES = new Set(['-', '—'])
const STACK_LABEL_BY_ID = APPROVAL_STACKS.reduce((acc, item) => {
  acc[item.id] = item.label
  return acc
}, {} as Record<ProviderProfession, string>)
type RequestStackFilter = ProviderProfession | 'all'
const shouldHideRequestItem = (item: AdminRequestItem) => {
  const email = String(item?.client?.email || '').trim().toLowerCase()
  if (REQUEST_EXCLUDED_EMAILS.has(email)) return true
  const clientName = String(item?.client?.name || '').trim()
  if (!clientName) return true
  if (REQUEST_EXCLUDED_CLIENT_NAMES.has(clientName)) return true
  return false
}

export default function AdminProviderEditor() {
  type ApprovalLookupState = {
    userId?: string
    status?: string
    name?: string
    email?: string
    updatedAt?: string
    displayProfession?: string
    note?: string
    error?: string
  }
  const [userId, setUserId] = useState('')
  const [email, setEmail] = useState('')
  const [adminAllowed, setAdminAllowed] = useState(false)
  const [profile, setProfile] = useState<any>(null)
  const [finProfile, setFinProfile] = useState<any>(null)
  const [propProfile, setPropProfile] = useState<any>(null)
  const [legalProfile, setLegalProfile] = useState<any>(null)
  const [techProfile, setTechProfile] = useState<any>(null)
  const [alliedProfile, setAlliedProfile] = useState<any>(null)
  const [reviews, setReviews] = useState<any[]>([])
  const [reviewsCursor, setReviewsCursor] = useState<string | null>(null)
  const [reviewsLoading, setReviewsLoading] = useState(false)
  const [reviewsError, setReviewsError] = useState<string | null>(null)
  const [reviewSaving, setReviewSaving] = useState(false)
  const [editReviewId, setEditReviewId] = useState<string | null>(null)
  const [editReviewFields, setEditReviewFields] = useState<{
    rating: string
    comment: string
    source: string
    reviewerId: string
    reviewerFirstName: string
    selectionId: string
    slotISO: string
    patientId: string
  }>({
    rating: '',
    comment: '',
    source: '',
    reviewerId: '',
    reviewerFirstName: '',
    selectionId: '',
    slotISO: '',
    patientId: '',
  })
  const [newReview, setNewReview] = useState({
    rating: '',
    comment: '',
    source: '',
    reviewerId: '',
    reviewerFirstName: '',
    selectionId: '',
    slotISO: '',
    patientId: '',
  })
  const [csvImportRows, setCsvImportRows] = useState<CsvReviewRow[]>([])
  const [csvImportFileName, setCsvImportFileName] = useState<string | null>(null)
  const [csvImportError, setCsvImportError] = useState<string | null>(null)
  const [csvImportNotice, setCsvImportNotice] = useState<string | null>(null)
  const [csvImportBusy, setCsvImportBusy] = useState(false)
  const [csvImportKey, setCsvImportKey] = useState(0)
  const [message, setMessage] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  type AdminProfession = ProviderProfession | 'app'
  const [profession, setProfession] = useState<AdminProfession>('education')
  const [eduProfile, setEduProfile] = useState<any>(null)
  const [migProfile, setMigProfile] = useState<any>(null)
  const [docProfile, setDocProfile] = useState<any>(null)
  const [photoKey, setPhotoKey] = useState<string | null>(null)
  const navigate = useNavigate()
  const [adminChecked, setAdminChecked] = useState(false)
  const [viewTab, setViewTab] = useState<'profile' | 'reviews' | 'approvals' | 'users' | 'provision' | 'requests' | 'stats' | 'emails'>('profile')
  const [approvalStacks, setApprovalStacks] = useState<ProviderProfession[]>(
    APPROVAL_STACKS.map((stack) => stack.id)
  )
  const [approvalResults, setApprovalResults] = useState<Record<string, ApprovalLookupState>>({})
  const [approvalDisplayResults, setApprovalDisplayResults] = useState<Record<string, ApprovalLookupState>>({})
  const [approvalComputedJson, setApprovalComputedJson] = useState('{}')
  const [displayLabelDrafts, setDisplayLabelDrafts] = useState<Record<string, string>>({})
  const approvalIdentityDraftPrefix = React.useMemo(() => {
    const uid = userId.trim()
    if (uid) return `uid:${uid}`
    const mail = email.trim().toLowerCase()
    if (mail) return `email:${mail}`
    return 'unknown'
  }, [userId, email])
  const displayLabelDraftKey = (stackId: ProviderProfession | string, identity = approvalIdentityDraftPrefix) =>
    `${identity}::${stackId}`
  const [userListFilter, setUserListFilter] = useState<'all' | 'providers-approved' | 'providers-pending'>('all')
  const [userListItems, setUserListItems] = useState<AdminUserListItem[]>([])
  const [userListLoading, setUserListLoading] = useState(false)
  const [userListError, setUserListError] = useState<string | null>(null)
  const [requestStack, setRequestStack] = useState<RequestStackFilter>('all')
  const [showFilteredRequests, setShowFilteredRequests] = useState(false)
  const [requestItems, setRequestItems] = useState<AdminRequestItem[]>([])
  const [requestCursor, setRequestCursor] = useState<string | null>(null)
  const [requestLoading, setRequestLoading] = useState(false)
  const [requestError, setRequestError] = useState<string | null>(null)
  const [statsStack, setStatsStack] = useState<ProviderProfession>(APPROVAL_STACKS[0]?.id || 'allied')
  const [statsItems, setStatsItems] = useState<AdminProviderStatsRow[]>([])
  const [statsCursor, setStatsCursor] = useState<string | null>(null)
  const [statsLoading, setStatsLoading] = useState(false)
  const [statsError, setStatsError] = useState<string | null>(null)
  const [emailStack, setEmailStack] = useState<EmailTemplateScope>(APPROVAL_STACKS[0]?.id || 'allied')
  const [emailTemplatesByStack, setEmailTemplatesByStack] = useState<Partial<Record<EmailTemplateScope, AdminEmailTemplateItem[]>>>({})
  const [emailTemplateId, setEmailTemplateId] = useState('')
  const [emailSubjectDraft, setEmailSubjectDraft] = useState('')
  const [emailHtmlDraft, setEmailHtmlDraft] = useState('')
  const [emailRollbackVersionId, setEmailRollbackVersionId] = useState('')
  const [emailPreviewRows, setEmailPreviewRows] = useState<Array<{
    stack: EmailTemplateScope
    ok: boolean
    unknownVariables: string[]
    missingVariables: string[]
    subjectPreview: string
    htmlPreview: string
    htmlPreviewWrapped?: string
    error?: string | null
  }>>([])
  const [emailLivePreviewHtml, setEmailLivePreviewHtml] = useState('')
  const emailLivePreviewSeqRef = React.useRef(0)
  const [emailLoading, setEmailLoading] = useState(false)
  const [emailPreviewing, setEmailPreviewing] = useState(false)
  const [emailSaving, setEmailSaving] = useState(false)
  const [emailMessage, setEmailMessage] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [showDeletedEmailTemplates, setShowDeletedEmailTemplates] = useState(false)
  const [provisionEmail, setProvisionEmail] = useState('')
  const [provisionFirstName, setProvisionFirstName] = useState('')
  const [provisionLastName, setProvisionLastName] = useState('')
  const [provisionStack, setProvisionStack] = useState<ProviderProfession>('allied')
  const [provisionLoading, setProvisionLoading] = useState(false)
  const [provisionMessage, setProvisionMessage] = useState<string | null>(null)
  const [provisionError, setProvisionError] = useState<string | null>(null)
  const [approvalLoading, setApprovalLoading] = useState(false)
  const [approvalMessage, setApprovalMessage] = useState<string | null>(null)
  const [approvalError, setApprovalError] = useState<string | null>(null)
  const approvalRequestSeqRef = React.useRef(0)
  const approvalSnapshotRef = React.useRef<Record<string, ApprovalLookupState>>({})
  const [approvalSnapshotVersion, setApprovalSnapshotVersion] = useState(0)
  const approvalPersistKey = React.useMemo(
    () => `approval-last-check::${approvalIdentityDraftPrefix}`,
    [approvalIdentityDraftPrefix]
  )
  const approvalPersistedResults = React.useMemo(() => {
    if (typeof window === 'undefined') return {}
    try {
      const raw = window.sessionStorage.getItem(approvalPersistKey) || '{}'
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, ApprovalLookupState>) : {}
    } catch {
      return {}
    }
  }, [approvalPersistKey, approvalComputedJson, approvalSnapshotVersion, approvalMessage])
  const approvalComputedResults = React.useMemo(() => {
    try {
      const parsed = JSON.parse(approvalComputedJson)
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, ApprovalLookupState>) : {}
    } catch {
      return {}
    }
  }, [approvalComputedJson])
  const approvalMessageResults = React.useMemo(() => {
    if (!approvalMessage) return {}
    const markerIndex = approvalMessage.indexOf(APPROVAL_MESSAGE_DATA_MARKER)
    if (markerIndex === -1) return {}
    const raw = approvalMessage.slice(markerIndex + APPROVAL_MESSAGE_DATA_MARKER.length)
    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, ApprovalLookupState>) : {}
    } catch {
      return {}
    }
  }, [approvalMessage])
  const approvalVisibleMessage = React.useMemo(() => {
    if (!approvalMessage) return null
    const markerIndex = approvalMessage.indexOf(APPROVAL_MESSAGE_DATA_MARKER)
    return markerIndex === -1 ? approvalMessage : approvalMessage.slice(0, markerIndex)
  }, [approvalMessage])
  const approvalComposeMessage = React.useCallback(
    (visibleMessage: string, results?: Record<string, ApprovalLookupState>) => {
      if (!results || !Object.keys(results).length) return visibleMessage
      return `${visibleMessage}${APPROVAL_MESSAGE_DATA_MARKER}${JSON.stringify(results)}`
    },
    []
  )
  const approvalRenderResult = React.useCallback(
    (stackId: ProviderProfession) => {
      const merged: ApprovalLookupState = {}
      ;[
        approvalResults[stackId],
        approvalDisplayResults[stackId],
        approvalSnapshotRef.current[stackId],
        approvalPersistedResults[stackId],
        approvalComputedResults[stackId],
        approvalMessageResults[stackId],
      ].forEach((source) => {
        if (!source || typeof source !== 'object') return
        APPROVAL_RESULT_FIELDS.forEach((field) => {
          if (source[field] !== undefined) merged[field] = source[field]
        })
      })
      return merged
    },
    [approvalComputedResults, approvalDisplayResults, approvalMessageResults, approvalPersistedResults, approvalResults]
  )
  const approvalComposeMergedMessage = React.useCallback(
    (
      visibleMessage: string,
      updates: Partial<Record<ProviderProfession, ApprovalLookupState>> = {},
      stackIds = approvalStacks
    ) => {
      const mergedResults = Object.fromEntries(
        stackIds.map((stackId) => [
          stackId,
          {
            ...approvalRenderResult(stackId),
            ...(updates[stackId] || {}),
          },
        ])
      ) as Record<string, ApprovalLookupState>
      return approvalComposeMessage(visibleMessage, mergedResults)
    },
    [approvalComposeMessage, approvalRenderResult, approvalStacks]
  )
  const summarizeApprovalResults = React.useCallback(
    (results: Record<string, ApprovalLookupState>) =>
      approvalStacks
        .map((stackId) => {
          const label = STACK_LABEL_BY_ID[stackId] || stackId
          const status = toSafeText(results[stackId]?.status, 'not checked') || 'not checked'
          return `${label}: ${status}`
        })
        .join(' · '),
    [approvalStacks]
  )
  const approvalDebugSummary = React.useMemo(
    () =>
      approvalStacks
        .map((stackId) => {
          const label = STACK_LABEL_BY_ID[stackId] || stackId
          const status = toSafeText(approvalRenderResult(stackId).status, 'not checked') || 'not checked'
          return `${label}: ${status}`
        })
        .join(' · '),
    [approvalRenderResult, approvalStacks, approvalSnapshotVersion]
  )
  const currentProfile = React.useMemo(() => {
    if (profession === 'education') return eduProfile?.profile
    if (profession === 'doctor') return docProfile?.profile
    if (profession === 'migration') return migProfile?.profile
    if (profession === 'tech') return techProfile?.profile
    if (profession === 'financial') return finProfile?.profile
    if (profession === 'property') return propProfile?.profile
    if (profession === 'legal') return legalProfile?.profile
    if (profession === 'allied') return alliedProfile?.profile
    return profile
  }, [profession, eduProfile, docProfile, migProfile, techProfile, finProfile, propProfile, legalProfile, alliedProfile, profile])
  const emailTemplates = React.useMemo(
    () => emailTemplatesByStack[emailStack] || [],
    [emailTemplatesByStack, emailStack]
  )
  const visibleEmailTemplates = React.useMemo(
    () =>
      showDeletedEmailTemplates
        ? emailTemplates
        : emailTemplates.filter((item) => !item.isDeleted),
    [emailTemplates, showDeletedEmailTemplates]
  )
  const deletedEmailTemplateCount = React.useMemo(
    () => emailTemplates.filter((item) => item.isDeleted).length,
    [emailTemplates]
  )
  const activeEmailTemplate = React.useMemo(
    () => visibleEmailTemplates.find((item) => item.template === emailTemplateId) || null,
    [visibleEmailTemplates, emailTemplateId]
  )
  const emailFamilyOptions = React.useMemo(() => {
    const byTemplate = new Map(visibleEmailTemplates.map((item) => [item.template, item]))
    const shared = SHARED_EMAIL_TEMPLATE_KEYS
      .map((template) => byTemplate.get(template))
      .filter(Boolean)
      .map((item) => ({
        value: item!.template,
        label: item!.title || item!.template,
        isShared: true,
      }))
    const stackSpecific = visibleEmailTemplates
      .filter((item) => !SHARED_EMAIL_TEMPLATE_SET.has(item.template))
      .map((item) => ({
        value: item.template,
        label: item.title || item.template,
        isShared: false,
      }))
    return [...shared, ...stackSpecific]
  }, [visibleEmailTemplates])
  const noVisibleEmailTemplatesMessage = React.useMemo(() => {
    if (!showDeletedEmailTemplates && deletedEmailTemplateCount > 0) {
      return 'No active email templates found. Enable "Show deleted templates" to restore one.'
    }
    return 'No email templates found.'
  }, [showDeletedEmailTemplates, deletedEmailTemplateCount])
  const emailDraftDirty = React.useMemo(
    () =>
      !!activeEmailTemplate &&
      (emailSubjectDraft !== (activeEmailTemplate.subjectTemplate || '') ||
        emailHtmlDraft !== (activeEmailTemplate.htmlTemplate || '')),
    [activeEmailTemplate, emailSubjectDraft, emailHtmlDraft]
  )
  const activeEmailVersions = React.useMemo(
    () =>
      Array.isArray(activeEmailTemplate?.versions)
        ? (activeEmailTemplate.versions as AdminEmailTemplateVersionItem[])
        : [],
    [activeEmailTemplate]
  )

  useEffect(() => {
    if (!visibleEmailTemplates.length) {
      if (!emailTemplateId && !emailSubjectDraft && !emailHtmlDraft && !emailRollbackVersionId) return
      setEmailTemplateId('')
      setEmailSubjectDraft('')
      setEmailHtmlDraft('')
      setEmailRollbackVersionId('')
      setEmailLivePreviewHtml('')
      return
    }
    const selected = visibleEmailTemplates.find((item) => item.template === emailTemplateId)
    if (selected) return
    const fallback = visibleEmailTemplates[0]
    setEmailTemplateId(fallback.template)
    setEmailSubjectDraft(fallback.subjectTemplate || '')
    setEmailHtmlDraft(fallback.htmlTemplate || '')
    setEmailRollbackVersionId('')
    setEmailLivePreviewHtml(fallback.htmlTemplate || '')
  }, [
    visibleEmailTemplates,
    emailTemplateId,
    emailSubjectDraft,
    emailHtmlDraft,
    emailRollbackVersionId,
    setEmailLivePreviewHtml,
  ])

  useEffect(() => {
    const allowlist = (import.meta.env.VITE_ADMIN_ALLOWLIST || '')
      .toLowerCase()
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    const sub = onAuthStateChanged(auth, (u) => {
      const emailLower = u?.email ? String(u.email).toLowerCase() : ''
      const allowed = !!emailLower && allowlist.includes(emailLower)
      setAdminAllowed(allowed)
      setAdminChecked(true)
      if (!allowed) {
        navigate('/', { replace: true })
      }
    })
    return () => sub()
  }, [navigate])

  useEffect(() => {
    if (profession === 'app') {
      setReviews([])
      setReviewsCursor(null)
      return
    }
    if (currentProfile?.userId) {
      loadReviews(true)
    } else {
      setReviews([])
      setReviewsCursor(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProfile?.userId, profession])

  useEffect(() => {
    setCsvImportRows([])
    setCsvImportFileName(null)
    setCsvImportError(null)
    setCsvImportNotice(null)
    setCsvImportKey((key) => key + 1)
  }, [currentProfile?.userId, profession])

  useEffect(() => {
    if (viewTab === 'users') {
      loadUserList(userListFilter)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewTab, userListFilter])

  useEffect(() => {
    if (viewTab === 'requests') {
      loadRequestList(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewTab, requestStack, showFilteredRequests])

  useEffect(() => {
    if (viewTab === 'stats') {
      loadStats(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewTab, statsStack])

  useEffect(() => {
    if (viewTab === 'emails') {
      loadEmailTemplates(emailStack)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewTab, emailStack])

  const resolveUidOrThrow = async () => {
    const uid = userId.trim()
    if (uid) return uid
    const mail = email.trim()
    if (!mail) {
      throw new Error('Enter provider UID or email')
    }
    const lookup = await adminGetProvider({ email: mail })
    const resolved =
      lookup?.profile?.userId ||
      lookup?.profile?.user_id ||
      lookup?.profile?.uid ||
      ''
    if (!resolved) {
      throw new Error('No provider found for that email')
    }
    setUserId(resolved)
    if (!email.trim() && lookup?.profile?.email) {
      setEmail(String(lookup.profile.email))
    }
    return resolved
  }

  const extractProfileUserId = (payload: any) => {
    const profile = payload?.profile || payload || {}
    return (
      profile.userId ||
      profile.user_id ||
      profile.uid ||
      ''
    )
  }

  const buildApprovalLookupResult = (payload: any) => {
    const profile = payload?.profile || payload?.draftProfile || {}
    const name = profile?.name || [profile?.firstName, profile?.lastName].filter(Boolean).join(' ')
    const rawStatus = payload?.status ?? profile?.status
    return {
      userId: toSafeText(extractProfileUserId(payload), ''),
      status: toSafeText(rawStatus, 'unknown') || 'unknown',
      name: toSafeText(name, ''),
      email: toSafeText(profile?.email, ''),
      updatedAt: toSafeText(profile?.updatedAt || profile?.createdAt, ''),
      displayProfession: toSafeText(profile?.displayProfession, ''),
    }
  }

  const resolveApprovalStackUserId = async (stackId: ProviderProfession) => {
    const resultUserId = toSafeText(approvalRenderResult(stackId).userId, '')
    if (resultUserId) return resultUserId

    const typedUserId = userId.trim()
    if (typedUserId) return typedUserId

    const mail = email.trim()
    if (mail) {
      // Resolve a canonical UID first so approve/unapprove works even when
      // stack email lookups return status metadata without profile identifiers.
      const canonicalUid = await resolveUidOrThrow()
      if (canonicalUid) {
        setApprovalResults((prev) => ({
          ...prev,
          [stackId]: {
            ...(prev[stackId] || {}),
            userId: canonicalUid,
            error: undefined,
          },
        }))
        setApprovalDisplayResults((prev) => ({
          ...prev,
          [stackId]: {
            ...(prev[stackId] || {}),
            userId: canonicalUid,
            error: undefined,
          },
        }))
        return canonicalUid
      }

      const res = await adminGetProviderByProfession(stackId, { email: mail })
      const resolved = toSafeText(extractProfileUserId(res), '')
      if (!resolved) throw new Error('Enter provider UID or email')

      const lookup = buildApprovalLookupResult(res)
      setApprovalResults((prev) => ({
        ...prev,
        [stackId]: {
          ...(prev[stackId] || {}),
          ...lookup,
          error: undefined,
        },
      }))
      setApprovalDisplayResults((prev) => ({
        ...prev,
        [stackId]: {
          ...(prev[stackId] || {}),
          ...lookup,
          error: undefined,
        },
      }))
      return resolved
    }

    throw new Error('Enter provider UID or email')
  }

  const applyStackProfile = (stackId: ProviderProfession, data: any) => {
    if (stackId === 'education') {
      setEduProfile(data)
      setMessage('Loaded education profile')
      return
    }
    if (stackId === 'doctor') {
      setDocProfile(data)
      setMessage('Loaded doctor profile')
      return
    }
    if (stackId === 'allied') {
      setAlliedProfile(data)
      setMessage('Loaded allied profile')
      return
    }
    if (stackId === 'migration') {
      setMigProfile(data)
      setMessage('Loaded migration profile')
      return
    }
    if (stackId === 'tech') {
      setTechProfile(data)
      setMessage('Loaded tech profile')
      return
    }
    if (stackId === 'financial') {
      setFinProfile(data)
      setMessage('Loaded financial profile')
      return
    }
    if (stackId === 'property') {
      setPropProfile(data)
      setMessage('Loaded property profile')
      return
    }
    if (stackId === 'legal') {
      setLegalProfile(data)
      setMessage('Loaded legal profile')
      return
    }
  }

  async function loadProfile() {
    setMessage(null)
    setErr(null)
    setProfile(null)
    setEduProfile(null)
    setMigProfile(null)
    setDocProfile(null)
    setPhotoKey(null)
    setFinProfile(null)
    setPropProfile(null)
    setLegalProfile(null)
    setTechProfile(null)
    setAlliedProfile(null)
    setReviews([])
    setReviewsCursor(null)
    setEditReviewId(null)
    setReviewsError(null)
    setLoading(true)
    try {
      const uid = userId.trim()
      const mail = email.trim()
      if (profession === 'app') {
        if (!uid && !mail) throw new Error('Enter provider UID or email')
        const data = await adminGetProvider({ userId: uid || undefined, email: mail || undefined })
        if (!data?.profile) throw new Error('Profile missing in response')
        setProfile(data.profile)
        const resolved = extractProfileUserId(data)
        if (resolved && !uid) {
          setUserId(String(resolved))
        }
        if (data.profile?.email && !mail) {
          setEmail(String(data.profile.email))
        }
        setMessage('Loaded profile')
        return
      }

      if (!uid && !mail) throw new Error('Enter provider UID or email')

      if (!uid && mail) {
        const data = await adminGetProviderByProfession(profession as ProviderProfession, { email: mail })
        if (!data?.profile) throw new Error('Profile missing in response')
        const resolved = extractProfileUserId(data)
        if (resolved) {
          setUserId(String(resolved))
        }
        if (data.profile?.email) {
          setEmail(String(data.profile.email))
        }
        applyStackProfile(profession as ProviderProfession, data)
        return
      }

      const resolvedUid = uid
      const data = await adminGetProviderByProfession(profession as ProviderProfession, {
        userId: resolvedUid || undefined,
        email: mail || undefined,
      })
      if (!data?.profile) throw new Error('Profile missing in response')
      applyStackProfile(profession as ProviderProfession, data)
    } catch (e: any) {
      setErr(e?.message || 'Load failed')
    } finally {
      setLoading(false)
    }
  }

  const toggleApprovalStack = (stackId: ProviderProfession) => {
    setApprovalStacks((prev) =>
      prev.includes(stackId) ? prev.filter((id) => id !== stackId) : [...prev, stackId]
    )
  }

  const selectAllApprovalStacks = () => {
    setApprovalStacks(APPROVAL_STACKS.map((stack) => stack.id))
  }

  const clearApprovalStacks = () => {
    setApprovalStacks([])
  }

  const clearApprovalState = () => {
    approvalRequestSeqRef.current += 1
    approvalSnapshotRef.current = {}
    setApprovalSnapshotVersion((value) => value + 1)
    setApprovalComputedJson('{}')
    if (typeof window !== 'undefined') {
      try {
        window.sessionStorage.removeItem(approvalPersistKey)
      } catch {
        // ignore storage errors
      }
    }
    setApprovalResults({})
    setApprovalDisplayResults({})
    setDisplayLabelDrafts({})
    setApprovalMessage(null)
    setApprovalError(null)
  }

  async function loadApprovalStatus() {
    setApprovalMessage(null)
    setApprovalError(null)
    let uid = userId.trim()
    const mail = email.trim()
    const selectedStacks = [...approvalStacks]
    if (!uid && mail) {
      try {
        uid = await resolveUidOrThrow()
      } catch {
        // keep email fallback when canonical lookup is unavailable
      }
    }
    const lookupParams = uid ? { userId: uid } : { email: mail }
    const requestId = approvalRequestSeqRef.current + 1
    approvalRequestSeqRef.current = requestId
    if (!approvalStacks.length) {
      setApprovalError('Select at least one stack to check')
      return
    }
    if (!uid && !mail) {
      setApprovalError('Enter provider UID or email')
      return
    }
    setApprovalLoading(true)
    try {
      const checkingResults = Object.fromEntries(
        selectedStacks.map((stackId) => [
          stackId,
          {
            status: 'checking',
            note: 'Checking status…',
            error: undefined,
            userId: undefined,
            name: '',
            email: '',
            updatedAt: '',
            displayProfession: '',
          },
        ])
      )
      setApprovalResults(checkingResults)
      setApprovalDisplayResults(checkingResults)
      approvalSnapshotRef.current = checkingResults
      setApprovalSnapshotVersion((value) => value + 1)
      setApprovalComputedJson(JSON.stringify(checkingResults))
      if (typeof window !== 'undefined') {
        try {
          window.sessionStorage.setItem(approvalPersistKey, JSON.stringify(checkingResults))
        } catch {
          // ignore storage errors
        }
      }
      const results = await Promise.all(
        selectedStacks.map(async (stackId) => {
          try {
            const res = await withTimeout(
              adminGetProviderByProfession(stackId, lookupParams),
              10000,
              `${APPROVAL_STACKS.find((item) => item.id === stackId)?.label || stackId} status check`
            )
            return [
              stackId,
              {
                ...buildApprovalLookupResult(res),
                note: undefined,
                error: undefined,
              },
            ] as const
          } catch (e: any) {
            const message = e?.message || 'Failed to load'
            const notFound = /not found/i.test(message)
            return [
              stackId,
              {
                status: notFound ? 'not found' : 'error',
                note: notFound ? 'No provider profile found in this stack' : undefined,
                error: notFound ? undefined : message,
                userId: undefined,
                name: '',
                email: '',
                updatedAt: '',
                displayProfession: '',
              },
            ] as const
          }
        })
      )
      if (approvalRequestSeqRef.current !== requestId) return

      const nextResults = Object.fromEntries(results)
      setApprovalResults(nextResults)
      setApprovalDisplayResults(nextResults)
      approvalSnapshotRef.current = nextResults
      setApprovalSnapshotVersion((value) => value + 1)
      setApprovalComputedJson(JSON.stringify(nextResults))
      if (typeof window !== 'undefined') {
        try {
          window.sessionStorage.setItem(approvalPersistKey, JSON.stringify(nextResults))
        } catch {
          // ignore storage errors
        }
      }
      setApprovalMessage(
        approvalComposeMessage(`Status check complete · ${summarizeApprovalResults(nextResults)}`, nextResults)
      )
      setDisplayLabelDrafts((prev) => {
        const next = { ...prev }
        selectedStacks.forEach((stackId) => {
          const displayProfession = nextResults[stackId]?.displayProfession
          if (displayProfession !== undefined) next[displayLabelDraftKey(stackId)] = displayProfession || ''
        })
        return next
      })
    } catch (e: any) {
      if (approvalRequestSeqRef.current !== requestId) return
      const message = e?.message || 'Failed to load approvals'
      setApprovalError(message)
      setApprovalResults((prev) => {
        const next = { ...prev }
        selectedStacks.forEach((stackId) => {
          const existing = next[stackId] || {}
          if (existing.status === 'checking') {
            next[stackId] = {
              ...existing,
              status: 'error',
              note: undefined,
              error: message,
            }
          }
        })
        return next
      })
      setApprovalDisplayResults((prev) => {
        const next = { ...prev }
        selectedStacks.forEach((stackId) => {
          const existing = next[stackId] || {}
          if (existing.status === 'checking') {
            next[stackId] = {
              ...existing,
              status: 'error',
              note: undefined,
              error: message,
            }
          }
        })
        return next
      })
      approvalSnapshotRef.current = selectedStacks.reduce<Record<string, ApprovalLookupState>>((next, stackId) => {
        const existing = approvalSnapshotRef.current[stackId] || {}
        next[stackId] =
          existing.status === 'checking'
            ? {
                ...existing,
                status: 'error',
                note: undefined,
                error: message,
              }
            : existing
        return next
      }, { ...approvalSnapshotRef.current })
      setApprovalSnapshotVersion((value) => value + 1)
      setApprovalComputedJson(JSON.stringify(approvalSnapshotRef.current))
      if (typeof window !== 'undefined') {
        try {
          window.sessionStorage.setItem(approvalPersistKey, JSON.stringify(approvalSnapshotRef.current))
        } catch {
          // ignore storage errors
        }
      }
    } finally {
      setApprovalLoading(false)
    }
  }

  async function approveSelectedStacks() {
    setApprovalMessage(null)
    setApprovalError(null)
    if (!approvalStacks.length) {
      setApprovalError('Select at least one stack to approve')
      return
    }
    setApprovalLoading(true)
    let approvedCount = 0
    let failedCount = 0
    const updates: Record<string, {
      userId?: string
      status?: string
      note?: string
      error?: string
    }> = {}
    for (const stackId of approvalStacks) {
      let stackUserId = ''
      try {
        stackUserId = await resolveApprovalStackUserId(stackId)
        const res = await adminApproveProvider(stackId, stackUserId)
        const already = Boolean(res?.already)
        updates[stackId] = {
          userId: stackUserId,
          status: 'approved',
          note: already ? 'Already approved' : undefined,
          error: undefined,
        }
        approvedCount += 1
      } catch (e: any) {
        failedCount += 1
        updates[stackId] = {
          ...(stackUserId ? { userId: stackUserId } : {}),
          error: e?.message || 'Approval failed',
        }
      }
    }
    setApprovalResults((prev) => {
      const next = { ...prev }
      Object.entries(updates).forEach(([stackId, update]) => {
        next[stackId] = {
          ...(prev[stackId] || {}),
          ...update,
        }
      })
      return next
    })
    setApprovalDisplayResults((prev) => {
      const next = { ...prev }
      Object.entries(updates).forEach(([stackId, update]) => {
        next[stackId] = {
          ...(prev[stackId] || {}),
          ...update,
        }
      })
      return next
    })
    if (approvedCount) {
      const suffix = failedCount ? ` · ${failedCount} failed` : ''
      setApprovalMessage(
        approvalComposeMergedMessage(`Approved ${approvedCount} stack${approvedCount === 1 ? '' : 's'}${suffix}`, updates)
      )
    } else if (failedCount) {
      setApprovalError('Approval failed for the selected stacks')
    }
    setApprovalLoading(false)
  }

  const userListOptions = [
    { value: 'all', label: 'All users (clients + providers)' },
    { value: 'providers-approved', label: 'Providers — approved' },
    { value: 'providers-pending', label: 'Providers — pending' },
  ] as const
  const userListFilterLabel = React.useMemo(
    () => userListOptions.find((opt) => opt.value === userListFilter)?.label || userListFilter,
    [userListFilter, userListOptions]
  )

  const loadUserList = async (filter = userListFilter) => {
    if (userListLoading) return
    setUserListError(null)
    setUserListLoading(true)
    try {
      const params: { role?: 'all' | 'clients' | 'providers'; status?: 'approved' | 'pending'; sort?: 'created' } = {
        sort: 'created',
      }
      if (filter === 'all') {
        params.role = 'all'
      } else if (filter === 'providers-approved') {
        params.role = 'providers'
        params.status = 'approved'
      } else if (filter === 'providers-pending') {
        params.role = 'providers'
        params.status = 'pending'
      }
      const res = await adminListUsers(params)
      const items = Array.isArray(res?.items) ? res.items : []
      setUserListItems(items)
    } catch (e: any) {
      setUserListError(e?.message || 'Failed to load user list')
      setUserListItems([])
    } finally {
      setUserListLoading(false)
    }
  }

  const handleExportUserList = () => {
    if (!userListItems.length) return
    const headers = [
      'Name',
      'Email',
      'Role',
      'Stack',
      'Status',
      'Created',
      'User ID',
    ]
    const rows = userListItems.map((item) => {
      const scope = item.scope === 'provider' ? 'Provider' : item.scope === 'client' ? 'Client' : (item.scope || '')
      const stack =
        item.scope === 'provider'
          ? item.scopeLabel || item.stackId || ''
          : item.scopeLabel || 'Match'
      return [
        formatCsvCell(item.name || ''),
        formatCsvCell(item.email || ''),
        formatCsvCell(scope),
        formatCsvCell(stack),
        formatCsvCell(item.status || ''),
        formatCsvCell(item.createdAt || ''),
        formatCsvCell(item.userId || ''),
      ].join(',')
    })
    const safeFilter = userListFilter.replace(/[^a-z0-9_-]+/gi, '-')
    const stamp = new Date().toISOString().slice(0, 10)
    const filename = `euca-users-${safeFilter}-${stamp}.csv`
    downloadCsv(filename, [headers.join(','), ...rows])
  }

  const loadRequestList = async (reset = true) => {
    if (requestLoading) return
    setRequestError(null)
    setRequestLoading(true)
    if (reset) {
      setRequestItems([])
      setRequestCursor(null)
    }
    try {
      if (requestStack === 'all') {
        const settled = await Promise.allSettled(
          APPROVAL_STACKS.map((stack) => adminListRequestsByProfession(stack.id))
        )
        const merged: AdminRequestItem[] = []
        const failedStacks: string[] = []
        settled.forEach((result, index) => {
          const stackId = APPROVAL_STACKS[index]?.id
          if (!stackId) return
          if (result.status !== 'fulfilled') {
            failedStacks.push(STACK_LABEL_BY_ID[stackId] || stackId)
            return
          }
          const stackItems = Array.isArray(result.value?.items) ? result.value.items : []
          stackItems.forEach((item) => {
            if (!showFilteredRequests && shouldHideRequestItem(item)) return
            merged.push({
              ...item,
              stackId,
              stackLabel: STACK_LABEL_BY_ID[stackId] || stackId,
            })
          })
        })
        setRequestItems(merged)
        setRequestCursor(null)
        if (failedStacks.length) {
          setRequestError(`Failed to load: ${failedStacks.join(', ')}`)
        }
        return
      }

      const res = await adminListRequestsByProfession(requestStack, {
        cursor: reset ? undefined : requestCursor || undefined,
      })
      const stackItems = Array.isArray(res?.items) ? res.items : []
      const normalized: AdminRequestItem[] = stackItems
        .filter((item) => showFilteredRequests || !shouldHideRequestItem(item))
        .map((item) => ({
          ...item,
          stackId: requestStack,
          stackLabel: STACK_LABEL_BY_ID[requestStack] || requestStack,
        }))
      setRequestItems((prev) => (reset ? normalized : [...prev, ...normalized]))
      setRequestCursor(res?.nextCursor || null)
    } catch (e: any) {
      setRequestError(e?.message || 'Failed to load requests')
      if (reset) setRequestItems([])
    } finally {
      setRequestLoading(false)
    }
  }

  const formatStatDate = (value?: string | null) => {
    if (!value) return '—'
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return '—'
    return parsed.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })
  }

  const renderCountCell = (row: AdminProviderStatsRow, key: string) => {
    const total = row.totals?.[key] ?? 0
    const last30 = row.window30?.[key] ?? 0
    const last90 = row.window90?.[key] ?? 0
    return (
      <div>
        <div style={{ fontWeight: 600 }}>{total}</div>
        <div className="muted" style={{ fontSize: '0.75rem' }}>
          30d {last30} · 90d {last90}
        </div>
      </div>
    )
  }

  const loadStats = async (reset = true) => {
    if (statsLoading) return
    setStatsError(null)
    setStatsLoading(true)
    if (reset) {
      setStatsItems([])
      setStatsCursor(null)
    }
    try {
      const res = await adminListProviderStats(statsStack, {
        cursor: reset ? undefined : statsCursor || undefined,
      })
      const items = Array.isArray(res?.items) ? res.items : []
      setStatsItems((prev) => (reset ? items : [...prev, ...items]))
      setStatsCursor(res?.nextCursor || null)
    } catch (e: any) {
      setStatsError(e?.message || 'Failed to load provider stats')
      if (reset) setStatsItems([])
    } finally {
      setStatsLoading(false)
    }
  }

  const selectEmailTemplate = (
    templateId: string,
    source = emailTemplates,
    stackId: EmailTemplateScope = emailStack
  ) => {
    const next = source.find((item) => item.template === templateId) || null
    if (!next) return
    setEmailTemplateId(next.template)
    setEmailSubjectDraft(next.subjectTemplate || '')
    setEmailHtmlDraft(next.htmlTemplate || '')
    setEmailRollbackVersionId('')
    setEmailPreviewRows((prev) => prev.filter((row) => row.stack !== stackId))
    setEmailLivePreviewHtml(next.htmlTemplate || '')
    setEmailMessage(null)
    setEmailError(null)
  }

  const loadEmailTemplates = async (
    stackId: EmailTemplateScope = emailStack,
    options: { syncDraft?: boolean } = {}
  ) => {
    if (emailLoading) return
    setEmailError(null)
    setEmailLoading(true)
    try {
      const res =
        stackId === 'app'
          ? await adminListEmailTemplates({ includeDeleted: true })
          : await adminListStackEmailTemplates(stackId, { includeDeleted: true })
      const items = Array.isArray(res?.items) ? res.items : []
      const visibleItems = showDeletedEmailTemplates
        ? items
        : items.filter((item) => !item.isDeleted)
      setEmailTemplatesByStack((prev) => ({ ...prev, [stackId]: items }))
      if (stackId === emailStack && options.syncDraft !== false) {
        if (!visibleItems.length) {
          setEmailTemplateId('')
          setEmailSubjectDraft('')
          setEmailHtmlDraft('')
          setEmailRollbackVersionId('')
          setEmailLivePreviewHtml('')
        } else {
          const selected = visibleItems.find((item) => item.template === emailTemplateId) || visibleItems[0]
          setEmailTemplateId(selected.template)
          setEmailSubjectDraft(selected.subjectTemplate || '')
          setEmailHtmlDraft(selected.htmlTemplate || '')
          setEmailRollbackVersionId('')
          setEmailLivePreviewHtml(selected.htmlTemplate || '')
        }
      }
      return items
    } catch (e: any) {
      if (stackId === emailStack) {
        setEmailError(e?.message || 'Failed to load email templates')
        setEmailTemplateId('')
        setEmailSubjectDraft('')
        setEmailHtmlDraft('')
        setEmailRollbackVersionId('')
        setEmailLivePreviewHtml('')
      }
      setEmailTemplatesByStack((prev) => ({ ...prev, [stackId]: [] }))
      return []
    } finally {
      setEmailLoading(false)
    }
  }

  const updateStackTemplateCache = React.useCallback(
    (stackId: EmailTemplateScope, updated: AdminEmailTemplateItem | null) => {
      if (!updated) return
      setEmailTemplatesByStack((prev) => {
        const current = prev[stackId] || []
        const hasItem = current.some((item) => item.template === updated.template)
        const nextItems = hasItem
          ? current.map((item) => (item.template === updated.template ? updated : item))
          : [...current, updated]
        return { ...prev, [stackId]: nextItems }
      })
      if (stackId === emailStack && updated.template === emailTemplateId) {
        setEmailSubjectDraft(updated.subjectTemplate || '')
        setEmailHtmlDraft(updated.htmlTemplate || '')
        setEmailLivePreviewHtml(updated.htmlTemplate || '')
      }
    },
    [emailStack, emailTemplateId]
  )

  useEffect(() => {
    if (viewTab !== 'emails') return
    if (!emailTemplateId) {
      setEmailLivePreviewHtml(emailHtmlDraft || '')
      return
    }

    const seq = ++emailLivePreviewSeqRef.current
    const timer = window.setTimeout(async () => {
      try {
        const preview =
          emailStack === 'app'
            ? await adminPreviewEmailTemplate({
                template: emailTemplateId,
                subjectTemplate: emailSubjectDraft,
                htmlTemplate: emailHtmlDraft,
              })
            : await adminPreviewStackEmailTemplate(emailStack, {
                template: emailTemplateId,
                subjectTemplate: emailSubjectDraft,
                htmlTemplate: emailHtmlDraft,
              })
        if (emailLivePreviewSeqRef.current !== seq) return
        const wrapped = String(preview?.htmlPreviewWrapped || preview?.htmlPreview || '').trim()
        setEmailLivePreviewHtml(wrapped || (emailHtmlDraft || ''))
      } catch {
        if (emailLivePreviewSeqRef.current !== seq) return
        setEmailLivePreviewHtml(emailHtmlDraft || '')
      }
    }, 280)

    return () => window.clearTimeout(timer)
  }, [viewTab, emailStack, emailTemplateId, emailSubjectDraft, emailHtmlDraft])

  const runEmailPreview = async (forAllStacks = false) => {
    if (!emailTemplateId || emailPreviewing) return
    const stacks: EmailTemplateScope[] = forAllStacks ? EMAIL_STACK_IDS : [emailStack]
    setEmailPreviewing(true)
    setEmailError(null)
    try {
      const settled = await Promise.allSettled(
        stacks.map(async (stackId) => {
          const preview =
            stackId === 'app'
              ? await adminPreviewEmailTemplate({
                  template: emailTemplateId,
                  subjectTemplate: emailSubjectDraft,
                  htmlTemplate: emailHtmlDraft,
                })
              : await adminPreviewStackEmailTemplate(stackId, {
                  template: emailTemplateId,
                  subjectTemplate: emailSubjectDraft,
                  htmlTemplate: emailHtmlDraft,
                })
          return {
            stack: stackId,
            ok: true,
            unknownVariables: Array.isArray(preview?.unknownVariables) ? preview.unknownVariables : [],
            missingVariables: Array.isArray(preview?.missingVariables) ? preview.missingVariables : [],
            subjectPreview: preview?.subjectPreview || '',
            htmlPreview: preview?.htmlPreview || '',
            htmlPreviewWrapped: preview?.htmlPreviewWrapped || preview?.htmlPreview || '',
            error: null as string | null,
          }
        })
      )
      const rows = settled.map((result, idx) => {
        if (result.status === 'fulfilled') return result.value
        return {
          stack: stacks[idx],
          ok: false,
          unknownVariables: [],
          missingVariables: [],
          subjectPreview: '',
          htmlPreview: '',
          htmlPreviewWrapped: '',
          error: result.reason?.message || 'Preview failed',
        }
      })
      setEmailPreviewRows(rows)
      const hasErrors = rows.some((row) => row.error || row.unknownVariables.length > 0)
      if (hasErrors) {
        setEmailError('Preview found issues. Fix variables before saving.')
      } else {
        setEmailMessage(forAllStacks ? 'Preview ready across all stacks' : 'Preview ready')
      }
      return rows
    } finally {
      setEmailPreviewing(false)
    }
  }

  const saveEmailTemplate = async () => {
    if (!activeEmailTemplate || emailSaving) return
    const subjectTemplate = emailSubjectDraft.trim()
    const htmlTemplate = emailHtmlDraft.trim()
    if (!subjectTemplate || !htmlTemplate) {
      setEmailError('Subject and email body are required')
      return
    }
    setEmailSaving(true)
    setEmailError(null)
    setEmailMessage(null)
    try {
      const res =
        emailStack === 'app'
          ? await adminSaveEmailTemplate({
              template: activeEmailTemplate.template,
              subjectTemplate,
              htmlTemplate,
            })
          : await adminSaveStackEmailTemplate(emailStack, {
              template: activeEmailTemplate.template,
              subjectTemplate,
              htmlTemplate,
            })
      const updated = res?.item || null
      if (updated) {
        updateStackTemplateCache(emailStack, updated)
      } else {
        await loadEmailTemplates(emailStack)
      }
      setEmailMessage(`Saved "${activeEmailTemplate.title}" for ${emailStack}`)
    } catch (e: any) {
      setEmailError(e?.message || 'Failed to save email template')
    } finally {
      setEmailSaving(false)
    }
  }

  const saveEmailTemplateAllStacks = async () => {
    if (!activeEmailTemplate || emailSaving) return
    if (emailStack === 'app') {
      setEmailError('Apply-to-all only targets the 8 profession stacks. Switch stack scope first.')
      return
    }
    if (!SHARED_EMAIL_TEMPLATE_SET.has(emailTemplateId)) {
      setEmailError('This template is stack-specific and cannot be applied to all stacks.')
      return
    }
    const subjectTemplate = emailSubjectDraft.trim()
    const htmlTemplate = emailHtmlDraft.trim()
    if (!subjectTemplate || !htmlTemplate) {
      setEmailError('Subject and email body are required')
      return
    }

    setEmailSaving(true)
    setEmailError(null)
    setEmailMessage(null)
    try {
      const previewRows = await runEmailPreview(true)
      const hasPreviewIssues = (previewRows || []).some(
        (row) => row.error || (row.unknownVariables && row.unknownVariables.length > 0)
      )
      if (hasPreviewIssues) {
        setEmailError('Cannot apply to all stacks until preview issues are resolved.')
        return
      }

      const settled = await Promise.allSettled(
        EMAIL_STACK_IDS.map(async (stackId) => {
          const res = await adminSaveStackEmailTemplate(stackId, {
            template: emailTemplateId,
            subjectTemplate,
            htmlTemplate,
          })
          return { stackId, item: res?.item || null }
        })
      )

      let okCount = 0
      let failedCount = 0
      const failedStacks: string[] = []
      settled.forEach((result) => {
        if (result.status === 'fulfilled') {
          okCount += 1
          if (result.value.item) updateStackTemplateCache(result.value.stackId, result.value.item)
        } else {
          failedCount += 1
          failedStacks.push(result.reason?.message || 'unknown error')
        }
      })
      const suffix = failedCount ? ` · ${failedCount} failed` : ''
      setEmailMessage(`Applied template to ${okCount} stacks${suffix}`)
      if (failedCount) {
        setEmailError(`Some stacks failed: ${failedStacks.join(' | ')}`)
      }
    } finally {
      setEmailSaving(false)
    }
  }

  const resetEmailTemplate = async () => {
    if (!activeEmailTemplate || emailSaving) return
    setEmailSaving(true)
    setEmailError(null)
    setEmailMessage(null)
    try {
      const res =
        emailStack === 'app'
          ? await adminSaveEmailTemplate({
              template: activeEmailTemplate.template,
              reset: true,
            })
          : await adminSaveStackEmailTemplate(emailStack, {
              template: activeEmailTemplate.template,
              reset: true,
            })
      const updated = res?.item || null
      if (updated) {
        updateStackTemplateCache(emailStack, updated)
      } else {
        await loadEmailTemplates(emailStack)
      }
      setEmailRollbackVersionId('')
      setEmailMessage(`Reset "${activeEmailTemplate.title}" for ${emailStack}`)
    } catch (e: any) {
      setEmailError(e?.message || 'Failed to reset email template')
    } finally {
      setEmailSaving(false)
    }
  }

  const rollbackEmailTemplate = async () => {
    if (!activeEmailTemplate || emailSaving) return
    if (emailStack === 'app') {
      setEmailError('Rollback history is only available on profession stack templates.')
      return
    }
    if (!emailRollbackVersionId) {
      setEmailError('Select a version to roll back to')
      return
    }
    setEmailSaving(true)
    setEmailError(null)
    setEmailMessage(null)
    try {
      const res = await adminSaveStackEmailTemplate(emailStack, {
        template: activeEmailTemplate.template,
        rollbackVersionId: emailRollbackVersionId,
      })
      const updated = res?.item || null
      if (updated) {
        updateStackTemplateCache(emailStack, updated)
      } else {
        await loadEmailTemplates(emailStack)
      }
      setEmailRollbackVersionId('')
      setEmailMessage(`Rolled back "${activeEmailTemplate.title}" on ${emailStack}`)
    } catch (e: any) {
      setEmailError(e?.message || 'Failed to roll back email template')
    } finally {
      setEmailSaving(false)
    }
  }

  const deleteEmailTemplate = async () => {
    if (!activeEmailTemplate || emailSaving) return
    const label = activeEmailTemplate.title || activeEmailTemplate.template
    const confirmed = window.confirm(
      `Delete "${label}" on ${emailStack}? This hides it and blocks future sends on that stack.`
    )
    if (!confirmed) return

    setEmailSaving(true)
    setEmailError(null)
    setEmailMessage(null)
    try {
      const res =
        emailStack === 'app'
          ? await adminSaveEmailTemplate({
              template: activeEmailTemplate.template,
              delete: true,
            })
          : await adminSaveStackEmailTemplate(emailStack, {
              template: activeEmailTemplate.template,
              delete: true,
            })
      const updated = res?.item || null
      if (updated) {
        updateStackTemplateCache(emailStack, updated)
      } else {
        await loadEmailTemplates(emailStack)
      }
      setEmailMessage(`Deleted "${label}" on ${emailStack}`)
    } catch (e: any) {
      setEmailError(e?.message || 'Failed to delete email template')
    } finally {
      setEmailSaving(false)
    }
  }

  const handleEmailTemplateImageUpload = React.useCallback(async () => null, [])
  const handleEmailTemplateAttachmentUpload = React.useCallback(async () => {}, [])

  const submitProvision = async () => {
    if (provisionLoading) return
    setProvisionMessage(null)
    setProvisionError(null)
    const emailVal = provisionEmail.trim().toLowerCase()
    const first = provisionFirstName.trim()
    const last = provisionLastName.trim()
    if (!emailVal || !first || !last) {
      setProvisionError('Provide email, first name, and last name')
      return
    }
    setProvisionLoading(true)
    try {
      const res = await adminProvisionProvider({
        email: emailVal,
        firstName: first,
        lastName: last,
        stackId: provisionStack,
      })
      const uid = res?.userId || ''
      if (uid) {
        setUserId(uid)
      }
      setProvisionMessage(`Provisioned ${first} ${last} (${provisionStack})`)
    } catch (e: any) {
      setProvisionError(e?.message || 'Provision failed')
    } finally {
      setProvisionLoading(false)
    }
  }

  const reviewStack = profession === 'app' ? null : (profession as any)
  const csvValidCount = csvImportRows.filter((row) => !row.errors?.length).length
  const csvInvalidCount = csvImportRows.length - csvValidCount
  const csvPreviewRows = csvImportRows.slice(0, 3)

  const formatRequestTime = (iso?: string | null) => {
    if (!iso) return '—'
    const ts = Date.parse(iso)
    if (!Number.isFinite(ts)) return '—'
    return new Date(ts).toLocaleString('en-AU', { timeZone: 'Australia/Sydney', hour12: false })
  }

  const formatFollowupResponse = (value?: string | null) => {
    if (!value) return '—'
    if (value === 'heard_back') return 'Yes'
    if (value === 'no_response') return 'No'
    return value
  }

  const formatResponseWithTime = (value?: string | null, at?: string | null) => {
    const label = formatFollowupResponse(value)
    if (label === '—') return '—'
    return at ? `${label} · ${formatRequestTime(at)}` : label
  }

  const getRequestedProfessionalNames = (item: AdminRequestItem) => {
    const professionals = Array.isArray(item.requestedProfessionals) ? item.requestedProfessionals : []
    const names = professionals
      .map((pro) =>
        String(
          pro?.name ||
            pro?.email ||
            pro?.specialistId ||
            ''
        ).trim()
      )
      .filter(Boolean)
    const uniqueNames = Array.from(new Set(names))
    if (uniqueNames.length) return uniqueNames
    const fallback = String(item.followupRoleLabel || item.specialty || '').trim()
    return fallback ? [fallback] : []
  }

  const getRequestedProfessionalDisplay = (item: AdminRequestItem) => {
    const names = getRequestedProfessionalNames(item)
    if (!names.length) return '—'
    if (names.length <= 2) return names.join(', ')
    return `${names[0]} +${names.length - 1} more`
  }

  const sortedRequestItems = React.useMemo(() => {
    return [...requestItems].sort((a, b) => {
      const aTime = Date.parse(a.createdAt || '') || 0
      const bTime = Date.parse(b.createdAt || '') || 0
      return bTime - aTime
    })
  }, [requestItems])

  const handleExportRequestList = () => {
    if (!sortedRequestItems.length) return
    const headers = [
      'Submitted',
      'Selection ID',
      'Stack',
      'Client Name',
      'Client Email',
      'Client Phone',
      'Professional requested',
      'Round 1',
      'Round 1 At',
      'Round 2',
      'Round 2 At',
      'Last response',
      'Last response At',
      'Specialty',
      'Reason',
      'Patient ID',
      'Updated At',
    ]
    const rows = sortedRequestItems.map((item) => [
      formatCsvCell(item.createdAt || ''),
      formatCsvCell(item.selectionId || ''),
      formatCsvCell(item.stackLabel || item.stackId || ''),
      formatCsvCell(item.client?.name || ''),
      formatCsvCell(item.client?.email || ''),
      formatCsvCell(item.client?.phone || ''),
      formatCsvCell(getRequestedProfessionalNames(item).join(' | ')),
      formatCsvCell(formatFollowupResponse(item.followupRound1Response)),
      formatCsvCell(item.followupRound1At || ''),
      formatCsvCell(formatFollowupResponse(item.followupRound2Response)),
      formatCsvCell(item.followupRound2At || ''),
      formatCsvCell(formatFollowupResponse(item.followupLastResponse)),
      formatCsvCell(item.followupLastAt || ''),
      formatCsvCell(item.specialty || ''),
      formatCsvCell(item.reason || ''),
      formatCsvCell(item.patientId || ''),
      formatCsvCell(item.updatedAt || ''),
    ].join(','))
    const stamp = new Date().toISOString().slice(0, 10)
    const filename = `euca-requests-${requestStack}-${stamp}.csv`
    downloadCsv(filename, [headers.join(','), ...rows])
  }

  async function loadReviews(reset = true) {
    if (!reviewStack || !currentProfile?.userId) return
    setReviewsLoading(true)
    setReviewsError(null)
    try {
      const res = await adminListProviderReviews(
        reviewStack,
        currentProfile.userId,
        reset ? undefined : reviewsCursor || undefined
      )
      const items = Array.isArray(res?.reviews) ? res.reviews : []
      const normalized = items.map((rev) => ({
        ...rev,
        reviewId: rev?.reviewId || rev?.id,
      }))
      setReviews((prev) => (reset ? normalized : [...prev, ...normalized]))
      setReviewsCursor(res?.nextCursor || null)
    } catch (e: any) {
      setReviewsError(e?.message || 'Failed to load reviews')
      if (reset) {
        setReviews([])
        setReviewsCursor(null)
      }
    } finally {
      setReviewsLoading(false)
    }
  }

  const startEditReview = (rev: any) => {
    setEditReviewId(rev.reviewId || rev.id)
    setEditReviewFields({
      rating: rev.rating != null ? String(rev.rating) : '',
      comment: rev.comment || '',
      source: rev.source || '',
      reviewerId: rev.reviewerId || '',
      reviewerFirstName: rev.reviewerFirstName || '',
      selectionId: rev.selectionId || rev.appointmentId || '',
      slotISO: rev.slotISO || '',
      patientId: rev.patientId || '',
    })
  }

  async function saveEditReview() {
    if (!editReviewId || !reviewStack) return
    setReviewSaving(true)
    setReviewsError(null)
    try {
      const payload: any = { reviewId: editReviewId }
      if (editReviewFields.rating) payload.rating = Number(editReviewFields.rating)
      if (editReviewFields.comment !== undefined) {
        payload.comment = editReviewFields.comment.trim() ? editReviewFields.comment : null
      }
      if (editReviewFields.source !== undefined) {
        payload.source = editReviewFields.source.trim() ? editReviewFields.source : null
      }
      if (editReviewFields.reviewerId !== undefined) {
        payload.reviewerId = editReviewFields.reviewerId.trim() ? editReviewFields.reviewerId.trim() : null
      }
      if (editReviewFields.reviewerFirstName !== undefined) {
        payload.reviewerFirstName = editReviewFields.reviewerFirstName.trim()
          ? editReviewFields.reviewerFirstName.trim()
          : null
      }
      if (editReviewFields.selectionId !== undefined) {
        const selectionId = editReviewFields.selectionId.trim()
        payload.selectionId = selectionId ? selectionId : null
        payload.appointmentId = selectionId ? selectionId : null
      }
      if (editReviewFields.slotISO !== undefined) {
        const slotISO = editReviewFields.slotISO.trim()
        if (!slotISO) {
          payload.slotISO = null
        } else if (slotISO === CONTACT_SLOT) {
          payload.slotISO = slotISO
        } else {
          payload.slotISO = normalizeSlotISO(slotISO)
        }
      }
      if (editReviewFields.patientId !== undefined) {
        payload.patientId = editReviewFields.patientId.trim() ? editReviewFields.patientId.trim() : null
      }
      await adminUpdateProviderReview(reviewStack, payload)
      setEditReviewId(null)
      await loadReviews(true)
    } catch (e: any) {
      setReviewsError(e?.message || 'Failed to update review')
    } finally {
      setReviewSaving(false)
    }
  }

  async function deleteReview(reviewId: string | undefined) {
    if (!reviewStack) return
    const resolvedId = String(reviewId || '').trim()
    if (!resolvedId) {
      setReviewsError('Missing reviewId for delete')
      return
    }
    setReviewSaving(true)
    setReviewsError(null)
    try {
      await adminDeleteProviderReview(reviewStack, { reviewId: resolvedId })
      setReviews((prev) => prev.filter((rev) => (rev.reviewId || rev.id) !== resolvedId))
      if (editReviewId === resolvedId) {
        setEditReviewId(null)
      }
      await loadReviews(true)
    } catch (e: any) {
      setReviewsError(e?.message || 'Failed to delete review')
    } finally {
      setReviewSaving(false)
    }
  }

  const normalizeSlotISO = (raw: string) => {
    const trimmed = String(raw || '').trim()
    if (!trimmed) return ''
    if (/[zZ]$/.test(trimmed) || /[+-]\d{2}:?\d{2}$/.test(trimmed)) return trimmed
    const parsed = new Date(trimmed)
    if (Number.isNaN(parsed.getTime())) throw new Error('Slot date is invalid')
    return parsed.toISOString()
  }

  const parseCsvReviews = (text: string) => {
    const table = parseCsvTable(text)
    if (!table.length) throw new Error('CSV appears empty.')
    const headerRow = table[0]
    const columnMap = headerRow.map((value) => csvHeaderMap[normalizeCsvHeader(value)] || null)
    if (!columnMap.includes('rating')) {
      throw new Error('CSV must include a rating column.')
    }
    const rows: CsvReviewRow[] = []
    for (let i = 1; i < table.length; i += 1) {
      const values = table[i]
      if (!values.some((value) => String(value || '').trim())) continue
      const row: CsvReviewRow = { rowNumber: i + 1 }
      const errors: string[] = []
      columnMap.forEach((field, index) => {
        if (!field) return
        const raw = values[index]
        const value = String(raw ?? '').trim()
        if (!value) return
        switch (field) {
          case 'rating': {
            const ratingNum = Number(value)
            if (!Number.isFinite(ratingNum)) {
              errors.push('Rating is invalid')
            } else {
              row.rating = ratingNum
            }
            break
          }
          case 'reviewerName':
            row.reviewerName = value
            break
          case 'reviewDateIso':
            row.reviewDateIso = value
            break
          case 'source':
            row.source = value
            break
          case 'comment':
            row.comment = value
            break
          case 'reviewerId':
            row.reviewerId = value
            break
          case 'selectionId':
            row.selectionId = value
            break
          case 'patientId':
            row.patientId = value
            break
          default:
            break
        }
      })
      if (row.rating == null) {
        errors.push('Rating is required')
      } else if (row.rating < 1 || row.rating > 5) {
        errors.push('Rating must be 1-5')
      }
      if (row.reviewDateIso) {
        try {
          row.reviewDateIso = normalizeSlotISO(row.reviewDateIso)
        } catch (e: any) {
          errors.push(e?.message || 'Date is invalid')
        }
      }
      row.errors = errors
      rows.push(row)
    }
    return rows
  }

  const handleCsvFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    setCsvImportError(null)
    setCsvImportNotice(null)
    setCsvImportFileName(file.name)
    try {
      const text = await file.text()
      const rows = parseCsvReviews(text)
      setCsvImportRows(rows)
      if (!rows.length) {
        setCsvImportNotice('No data rows found.')
        return
      }
      const validCount = rows.filter((row) => !row.errors?.length).length
      const invalidCount = rows.length - validCount
      setCsvImportNotice(
        `Loaded ${rows.length} row${rows.length === 1 ? '' : 's'} (${validCount} ready${invalidCount ? `, ${invalidCount} with issues` : ''}).`
      )
    } catch (e: any) {
      setCsvImportRows([])
      setCsvImportError(e?.message || 'Failed to parse CSV')
    } finally {
      event.target.value = ''
    }
  }

  const importCsvReviews = async () => {
    if (!reviewStack || !currentProfile?.userId) return
    const validRows = csvImportRows.filter((row) => !row.errors?.length)
    if (!validRows.length) {
      setCsvImportError('No valid rows to import.')
      return
    }
    setCsvImportBusy(true)
    setCsvImportError(null)
    setCsvImportNotice(null)
    let successCount = 0
    const failures: string[] = []
    try {
      for (const row of validRows) {
        try {
          if (row.rating == null) throw new Error('Missing rating')
          const payload: any = {
            revieweeId: currentProfile.userId,
            rating: row.rating,
          }
          if (row.comment) payload.comment = row.comment
          if (row.source) payload.source = row.source
          if (row.reviewerName) payload.reviewerFirstName = row.reviewerName
          if (row.reviewDateIso) payload.slotISO = row.reviewDateIso
          if (row.reviewerId) payload.reviewerId = row.reviewerId
          if (row.selectionId) payload.selectionId = row.selectionId
          if (row.patientId) payload.patientId = row.patientId
          await adminCreateProviderReview(reviewStack, payload)
          successCount += 1
        } catch (e: any) {
          failures.push(`Row ${row.rowNumber}: ${e?.message || 'Failed to import'}`)
        }
      }
      if (successCount) await loadReviews(true)
      const failureCount = failures.length
      setCsvImportNotice(
        `Imported ${successCount} review${successCount === 1 ? '' : 's'}${failureCount ? `, ${failureCount} failed.` : '.'}`
      )
      if (failureCount) {
        const preview = failures.slice(0, 3).join(' ')
        setCsvImportError(`${preview}${failureCount > 3 ? '…' : ''}`)
      } else {
        setCsvImportRows([])
        setCsvImportFileName(null)
        setCsvImportKey((key) => key + 1)
      }
    } finally {
      setCsvImportBusy(false)
    }
  }

  async function createReview() {
    if (!reviewStack || !currentProfile?.userId) return
    setReviewSaving(true)
    setReviewsError(null)
    try {
      const ratingNum = Number(newReview.rating)
      if (!Number.isFinite(ratingNum)) throw new Error('Rating is required')
      const payload: any = {
        revieweeId: currentProfile.userId,
        rating: ratingNum,
      }
      if (newReview.comment) payload.comment = newReview.comment
      if (newReview.source) payload.source = newReview.source
      if (newReview.reviewerId) payload.reviewerId = newReview.reviewerId
      if (newReview.reviewerFirstName) payload.reviewerFirstName = newReview.reviewerFirstName
      if (newReview.selectionId) payload.selectionId = newReview.selectionId
      if (newReview.slotISO) payload.slotISO = normalizeSlotISO(newReview.slotISO)
      if (newReview.patientId) payload.patientId = newReview.patientId
      await adminCreateProviderReview(reviewStack, payload)
      setNewReview({
        rating: '',
        comment: '',
        source: '',
        reviewerId: '',
        reviewerFirstName: '',
        selectionId: '',
        slotISO: '',
        patientId: '',
      })
      await loadReviews(true)
    } catch (e: any) {
      setReviewsError(e?.message || 'Failed to create review')
    } finally {
      setReviewSaving(false)
    }
  }

  async function handleUpload(file: File) {
    if (!profile?.userId) {
      setErr('Load a profile first')
      return
    }
    setErr(null)
    setMessage(null)
    try {
      const presign = await adminPresignProviderUpload({
        userId: profile.userId,
        keyHint: 'photo',
        contentType: file.type || 'image/jpeg',
      })
      const fd = new FormData()
      Object.entries(presign.fields || {}).forEach(([k, v]) => fd.append(k, v as any))
      fd.append('file', file)
      const up = await fetch(presign.url, { method: 'POST', body: fd })
      if (!up.ok) {
        const detail = await up.text()
        throw new Error(detail || 'Upload failed')
      }
      setPhotoKey(presign.key)
      setMessage('Photo uploaded. Remember to save changes.')
    } catch (e: any) {
      setErr(e?.message || 'Upload failed')
    }
  }

  const [basicFields, setBasicFields] = useState({
    name: '',
    bio: '',
    specialties: '',
    note: '',
    title: '',
    practiceName: '',
    abn: '',
    tradingName: '',
    registrationNumber: '',
    registrationAuthority: '',
    feeRange: '',
    pricingMin: '',
    pricingMax: '',
    city: '',
    state: '',
    postalCode: '',
    publicEmail: '',
    publicPhone: '',
    adminEmail: '',
    adminPhone: '',
    languages: '',
    modes: '',
    availabilityTags: '',
    extraJson: '{ }',
  })

  React.useEffect(() => {
    if (!profile) return
    setBasicFields({
      name: profile.name || profile.fullName || '',
      bio: profile.bio || profile.profile || '',
      specialties: Array.isArray(profile.specialties) ? profile.specialties.join('\n') : '',
      note: '',
      title: profile.title || '',
      practiceName: profile.practiceName || '',
      abn: profile.abn || '',
      tradingName: profile.tradingName || '',
      registrationNumber: profile.registrationNumber || profile.providerRegistrationNumber || '',
      registrationAuthority: profile.registrationAuthority || '',
      feeRange: profile.feeRange || '',
      pricingMin: profile.pricing?.minAud != null ? String(profile.pricing.minAud) : '',
      pricingMax: profile.pricing?.maxAud != null ? String(profile.pricing.maxAud) : '',
      city: profile.address?.city || profile.publicCity || '',
      state: profile.address?.state || profile.publicState || '',
      postalCode: profile.address?.postalCode || profile.address?.postcode || profile.address?.postCode || profile.publicPostcode || '',
      publicEmail: profile.publicContact?.email || profile.adminContact?.email || profile.email || '',
      publicPhone: profile.publicContact?.phone || profile.adminContact?.phone || profile.phone || '',
      adminEmail: profile.adminContact?.email || '',
      adminPhone: profile.adminContact?.phone || '',
      languages: Array.isArray(profile.languages) ? profile.languages.join('\n') : '',
      modes: Array.isArray(profile.modes) ? profile.modes.join('\n') : '',
      availabilityTags: Array.isArray(profile.availabilityTags) ? profile.availabilityTags.join('\n') : '',
      extraJson: '{ }',
    })
  }, [profile])

  React.useEffect(() => {
    if (!eduProfile || profession !== 'education') return
    setBasicFields({
      name: eduProfile.name || eduProfile.fullName || '',
      bio: eduProfile.bio || eduProfile.profile || '',
      specialties: Array.isArray(eduProfile.specialties) ? eduProfile.specialties.join('\n') : '',
      note: '',
      title: eduProfile.title || '',
      practiceName: eduProfile.practiceName || '',
      abn: eduProfile.abn || '',
      tradingName: eduProfile.tradingName || '',
      registrationNumber: eduProfile.registrationNumber || eduProfile.providerRegistrationNumber || '',
      registrationAuthority: eduProfile.registrationAuthority || '',
      feeRange: eduProfile.feeRange || '',
      pricingMin: eduProfile.pricing?.minAud != null ? String(eduProfile.pricing.minAud) : '',
      pricingMax: eduProfile.pricing?.maxAud != null ? String(eduProfile.pricing.maxAud) : '',
      city: eduProfile.address?.city || eduProfile.publicCity || '',
      state: eduProfile.address?.state || eduProfile.publicState || '',
      postalCode: eduProfile.address?.postalCode || eduProfile.address?.postcode || eduProfile.address?.postCode || eduProfile.publicPostcode || '',
      publicEmail: eduProfile.publicContact?.email || eduProfile.adminContact?.email || eduProfile.email || '',
      publicPhone: eduProfile.publicContact?.phone || eduProfile.adminContact?.phone || eduProfile.phone || '',
      adminEmail: eduProfile.adminContact?.email || '',
      adminPhone: eduProfile.adminContact?.phone || '',
      languages: Array.isArray(eduProfile.languages) ? eduProfile.languages.join('\n') : '',
      modes: Array.isArray(eduProfile.modes) ? eduProfile.modes.join('\n') : '',
      availabilityTags: Array.isArray(eduProfile.availabilityTags) ? eduProfile.availabilityTags.join('\n') : '',
      extraJson: '{ }',
    })
  }, [eduProfile, profession])

  async function saveBasic() {
    const currentProfile = profession === 'education' ? eduProfile : profession === 'migration' ? migProfile : profile
    if (!currentProfile?.userId) {
      setErr('Load a profile first')
      return
    }
    setSaving(true)
    setErr(null)
    setMessage(null)
    try {
      const updates: Record<string, any> = {}
      const f = basicFields
      if (f.name) updates.name = f.name
      if (f.bio) {
        updates.bio = f.bio
        updates.profile = f.bio
      }
      const specs = parseList(f.specialties)
      if (specs.length) updates.specialties = specs
      if (f.title) updates.title = f.title
      if (f.practiceName) updates.practiceName = f.practiceName
      if (f.abn) updates.abn = f.abn
      if (f.tradingName) updates.tradingName = f.tradingName
      if (f.registrationNumber) {
        updates.registrationNumber = f.registrationNumber
        updates.providerRegistrationNumber = f.registrationNumber
      }
      if (f.registrationAuthority) updates.registrationAuthority = f.registrationAuthority
      if (f.feeRange) updates.feeRange = f.feeRange
      const minAud = Number(f.pricingMin)
      const maxAud = Number(f.pricingMax)
      if (Number.isFinite(minAud) || Number.isFinite(maxAud)) {
        updates.pricing = {
          ...(currentProfile.pricing || {}),
          ...(Number.isFinite(minAud) ? { minAud } : {}),
          ...(Number.isFinite(maxAud) ? { maxAud } : {}),
        }
      }
      if (f.state || f.postalCode || f.city) {
        updates.address = { ...(currentProfile.address || {}), state: f.state, postalCode: f.postalCode, city: f.city }
      }
      if (f.city) updates.publicCity = f.city
      if (f.state) updates.publicState = f.state
      if (f.postalCode) updates.publicPostcode = f.postalCode
      if (f.publicEmail || f.publicPhone) {
        updates.publicContact = { ...(currentProfile.publicContact || {}), email: f.publicEmail, phone: f.publicPhone }
      }
      if (f.adminEmail || f.adminPhone) {
        updates.adminContact = { ...(currentProfile.adminContact || {}), email: f.adminEmail, phone: f.adminPhone }
      }
      const langs = parseList(f.languages)
      if (langs.length) updates.languages = langs
      const modes = parseList(f.modes)
      if (modes.length) updates.modes = modes
      const avail = parseList(f.availabilityTags)
      if (avail.length) updates.availabilityTags = avail
      if (photoKey) {
        updates.photoKey = photoKey
        updates.avatarKey = photoKey
        updates.documents = { ...(currentProfile.documents || {}), photoKey }
      }
      if (f.extraJson && f.extraJson.trim() && f.extraJson.trim() !== '{ }') {
        try {
          const extras = JSON.parse(f.extraJson)
          Object.assign(updates, extras)
        } catch (e: any) {
          throw new Error(`Extra JSON invalid: ${e?.message || 'parse error'}`)
        }
      }
      let res: any = null
      if (profession === 'education') {
        res = await adminEducationUpdateProvider(currentProfile.userId, updates)
        setEduProfile(res?.profile || currentProfile)
      } else if (profession === 'migration') {
        res = await adminMigrationUpdateProvider(currentProfile.userId, updates)
        setMigProfile(res?.profile || currentProfile)
      } else {
        res = await adminUpdateProvider({ userId: currentProfile.userId, updates, note: f.note || undefined })
        setProfile(res.profile || currentProfile)
      }
      setMessage('Saved')
    } catch (e: any) {
      setErr(e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const renderEducationEditor = () => {
    if (!eduProfile?.profile) {
      return (
        <div className="card" style={{ padding: 16 }}>
          {loading ? <Spinner label="Loading education profile…" /> : <div className="muted">Enter userId and load profile.</div>}
          {err && (
            <div className="error" role="alert" style={{ marginTop: 8 }}>
              {err}
            </div>
          )}
        </div>
      )
    }
    const initial = eduProfile.profile || eduProfile.draftProfile || {}
    const uid = eduProfile.profile.userId || userId.trim()
    return (
      <div className="card" style={{ padding: 16 }}>
        <RegisterEducation
          user={{ email: eduProfile.profile.email }}
          initialProfile={initial}
          adminMode
          adminPresign={(hint, contentType) => adminEducationPresignUpload({ userId: uid, keyHint: hint, contentType })}
          onAdminSubmit={(payload) => adminEducationUpdateProvider(uid, payload)}
        />
      </div>
    )
  }

  const renderMigrationEditor = () => {
    if (!migProfile?.profile) {
      return (
        <div className="card" style={{ padding: 16 }}>
          {loading ? <Spinner label="Loading migration profile…" /> : <div className="muted">Enter userId and load profile.</div>}
          {err && (
            <div className="error" role="alert" style={{ marginTop: 8 }}>
              {err}
            </div>
          )}
        </div>
      )
    }
    const initial = migProfile.profile || migProfile.draftProfile || {}
    const uid = migProfile.profile.userId || userId.trim()
    return (
      <div className="card" style={{ padding: 16 }}>
        <RegisterMigration
          user={{ email: migProfile.profile.email }}
          initialProfile={initial}
          adminMode
          adminPresign={(hint, contentType) => adminMigrationPresignUpload({ userId: uid, keyHint: hint, contentType })}
          onAdminSubmit={(payload) => adminMigrationUpdateProvider(uid, payload)}
        />
      </div>
    )
  }

  const renderDoctorEditor = () => {
    if (!docProfile?.profile) {
      return (
        <div className="card" style={{ padding: 16 }}>
          {loading ? <Spinner label="Loading doctor profile…" /> : <div className="muted">Enter userId and load profile.</div>}
          {err && (
            <div className="error" role="alert" style={{ marginTop: 8 }}>
              {err}
            </div>
          )}
        </div>
      )
    }
    const initial = docProfile.profile || docProfile.draftProfile || {}
    const uid = docProfile.profile.userId || userId.trim()
    return (
      <div className="card" style={{ padding: 16 }}>
        <RegisterDoctor
          user={{ email: docProfile.profile.email }}
          initialProfile={initial}
          adminMode
          adminPresign={(hint, contentType) => adminDoctorPresignUpload({ userId: uid, keyHint: hint, contentType })}
          onAdminSubmit={(payload) => adminDoctorUpdateProvider(uid, payload)}
          inlineNotice={<span>Admin editing doctor profile</span>}
        />
      </div>
    )
  }

  const renderTechEditor = () => {
    if (!techProfile?.profile) {
      return (
        <div className="card" style={{ padding: 16 }}>
          {loading ? <Spinner label="Loading tech profile…" /> : <div className="muted">Enter userId and load profile.</div>}
          {err && (
            <div className="error" role="alert" style={{ marginTop: 8 }}>
              {err}
            </div>
          )}
        </div>
      )
    }
    const initial = techProfile.profile || techProfile.draftProfile || {}
    const uid = techProfile.profile.userId || userId.trim()
    return (
      <div className="card" style={{ padding: 16 }}>
        <RegisterTech
          user={{ email: techProfile.profile.email }}
          initialProfile={initial}
          adminMode
          adminPresign={(hint, contentType) => adminTechPresignUpload({ userId: uid, keyHint: hint, contentType })}
          onAdminSubmit={(payload) => adminTechUpdateProvider(uid, payload)}
        />
      </div>
    )
  }

  const renderAlliedEditor = () => {
    if (!alliedProfile?.profile) {
      return (
        <div className="card" style={{ padding: 16 }}>
          {loading ? <Spinner label="Loading allied profile…" /> : <div className="muted">Enter userId and load profile.</div>}
          {err && (
            <div className="error" role="alert" style={{ marginTop: 8 }}>
              {err}
            </div>
          )}
        </div>
      )
    }
    const initial = alliedProfile.profile || alliedProfile.draftProfile || {}
    const uid = alliedProfile.profile.userId || userId.trim()
    return (
      <div className="card" style={{ padding: 16 }}>
        <RegisterAllied
          user={{ email: alliedProfile.profile.email }}
          initialProfile={initial}
          adminMode
          adminPresign={(hint, contentType) => adminAlliedPresignUpload({ userId: uid, keyHint: hint, contentType })}
          onAdminSubmit={(payload) => adminAlliedUpdateProvider(uid, payload)}
        />
      </div>
    )
  }

  const needsDockClearance =
    viewTab === 'reviews' ||
    viewTab === 'approvals' ||
    viewTab === 'users' ||
    viewTab === 'provision' ||
    viewTab === 'requests' ||
    viewTab === 'stats' ||
    viewTab === 'emails'
  const pagePaddingBottom = needsDockClearance ? 220 : 24

  if (!adminChecked) {
    return (
      <div className="page" style={{ maxWidth: 980, margin: '0 auto', padding: '24px 16px' }}>
        <Spinner label="Checking admin access…" />
      </div>
    )
  }

  return (
    <div
      className="page"
      style={{
        maxWidth: 980,
        margin: '0 auto',
        paddingTop: 24,
        paddingRight: 16,
        paddingLeft: 16,
        paddingBottom: pagePaddingBottom,
      }}
    >
      {!adminAllowed && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div className="error" role="alert">
            Admin access required. Please sign in with an approved admin account.
          </div>
        </div>
      )}
      {adminAllowed && (
        <>
      <h1 style={{ marginBottom: 12 }}>Admin · Edit Provider</h1>
      <p className="muted" style={{ marginBottom: 16 }}>
        Admin-only. Choose profession, load by UID/email, and edit. Education uses the full registration form; “App” is a lightweight user record edit.
      </p>

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <button
            className={`btn ${viewTab === 'profile' ? 'btn-primary' : 'btn-outline'}`}
            type="button"
            onClick={() => setViewTab('profile')}
          >
            Profile
          </button>
          <button
            className={`btn ${viewTab === 'reviews' ? 'btn-primary' : 'btn-outline'}`}
            type="button"
            onClick={() => setViewTab('reviews')}
          >
            Reviews
          </button>
          <button
            className={`btn ${viewTab === 'approvals' ? 'btn-primary' : 'btn-outline'}`}
            type="button"
            onClick={() => setViewTab('approvals')}
          >
            Approvals
          </button>
          <button
            className={`btn ${viewTab === 'users' ? 'btn-primary' : 'btn-outline'}`}
            type="button"
            onClick={() => setViewTab('users')}
          >
            Users
          </button>
          <button
            className={`btn ${viewTab === 'requests' ? 'btn-primary' : 'btn-outline'}`}
            type="button"
            onClick={() => setViewTab('requests')}
          >
            Requests
          </button>
          <button
            className={`btn ${viewTab === 'stats' ? 'btn-primary' : 'btn-outline'}`}
            type="button"
            onClick={() => setViewTab('stats')}
          >
            Stats
          </button>
          <button
            className={`btn ${viewTab === 'emails' ? 'btn-primary' : 'btn-outline'}`}
            type="button"
            onClick={() => setViewTab('emails')}
          >
            Emails
          </button>
          <button
            className={`btn ${viewTab === 'provision' ? 'btn-primary' : 'btn-outline'}`}
            type="button"
            onClick={() => setViewTab('provision')}
          >
            Provision
          </button>
        </div>
        {(viewTab === 'profile' || viewTab === 'reviews' || viewTab === 'approvals') && (
          <>
            <div className="field">
              <label className="field-label">Profession</label>
              <select className="input" value={profession} onChange={(e) => setProfession(e.target.value as any)}>
                <option value="education">Education (full form)</option>
                <option value="doctor">Doctor (full form)</option>
                <option value="allied">Allied Health (full form)</option>
                <option value="migration">Migration (full form)</option>
                <option value="tech">Tech, Creative & Media (full form)</option>
                <option value="financial">Financial (full form)</option>
                <option value="property">Property (full form)</option>
                <option value="legal">Legal, Governance & Compliance (full form)</option>
                <option value="app">App (basic record)</option>
              </select>
            </div>
            <div className="field">
              <label className="field-label">Provider UID</label>
              <input
                className="input"
                value={userId}
                onChange={(e) => {
                  setUserId(e.target.value)
                  clearApprovalState()
                }}
                placeholder="userId"
              />
            </div>
            <div className="field">
              <label className="field-label">Email (optional for App)</label>
              <input
                className="input"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  clearApprovalState()
                }}
                placeholder="email@example.com"
              />
            </div>
            <button className="btn btn-primary" type="button" onClick={loadProfile} disabled={loading}>
              {loading ? <Spinner size={16} /> : 'Load profile'}
            </button>
          </>
        )}
      </div>

      {viewTab === 'profile' ? (
        profession === 'education' ? (
          renderEducationEditor()
        ) : profession === 'doctor' ? (
          renderDoctorEditor()
        ) : profession === 'allied' ? (
          renderAlliedEditor()
        ) : profession === 'migration' ? (
          renderMigrationEditor()
        ) : profession === 'tech' ? (
          renderTechEditor()
        ) : profession === 'financial' ? (
          finProfile ? (
            <div className="card" style={{ padding: 0 }}>
              <RegisterFinancial
                adminMode
                user={null}
                initialProfile={finProfile}
                onAdminSubmit={async (payload) =>
                  adminFinancialUpdateProvider(
                    finProfile.profile?.userId || finProfile.profile?.user_id || userId,
                    payload
                  )}
                adminPresign={async (keyHint, contentType) =>
                  adminFinancialPresignUpload({
                    userId: finProfile.profile?.userId || finProfile.profile?.user_id || userId,
                    keyHint,
                    contentType,
                  })
                }
                inlineNotice={<span>Admin editing financial profile</span>}
              />
            </div>
          ) : null
        ) : profession === 'property' ? (
          propProfile ? (
            <div className="card" style={{ padding: 0 }}>
              <RegisterProperty
                adminMode
                user={{ email: propProfile.profile?.email }}
                initialProfile={propProfile.profile || propProfile.draftProfile}
                onAdminSubmit={async (payload) =>
                  adminPropertyUpdateProvider(propProfile.profile.userId || propProfile.profile.user_id || userId, payload)
                }
                adminPresign={async (keyHint, contentType) =>
                  adminPropertyPresignUpload({
                    userId: propProfile.profile.userId || propProfile.profile.user_id || userId,
                    keyHint,
                    contentType,
                  })
                }
                inlineNotice={<span>Admin editing property profile</span>}
              />
            </div>
          ) : null
        ) : profession === 'legal' ? (
          legalProfile ? (
            <div className="card" style={{ padding: 0 }}>
              <RegisterLegal
                adminMode
                user={{ email: legalProfile.profile?.email }}
                initialProfile={legalProfile.profile || legalProfile.draftProfile}
                onAdminSubmit={async (payload) =>
                  adminLegalUpdateProvider(legalProfile.profile.userId || legalProfile.profile.user_id || userId, payload)
                }
                adminPresign={async (keyHint, contentType) =>
                  adminLegalPresignUpload({
                    userId: legalProfile.profile.userId || legalProfile.profile.user_id || userId,
                    keyHint,
                    contentType,
                  })
                }
                inlineNotice={<span>Admin editing legal profile</span>}
              />
            </div>
          ) : null
        ) : profile ? (
          <div className="card" style={{ padding: 16 }}>
            <div className="field">
              <label className="field-label">Name</label>
              <input className="input" value={basicFields.name} onChange={(e) => setBasicFields({ ...basicFields, name: e.target.value })} />
            </div>
            <div className="field">
              <label className="field-label">Bio / Profile</label>
              <textarea className="input" rows={6} value={basicFields.bio} onChange={(e) => setBasicFields({ ...basicFields, bio: e.target.value })} />
            </div>
            <div className="field">
              <label className="field-label">Areas of expertise (one per line or comma)</label>
              <textarea
                className="input"
                rows={6}
                value={basicFields.specialties}
                onChange={(e) => setBasicFields({ ...basicFields, specialties: e.target.value })}
                placeholder="e.g. Maths tutoring&#10;Science tutoring"
              />
            </div>
            <div className="field">
              <label className="field-label">Profile photo</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) handleUpload(f)
                }}
              />
            </div>
            <div className="field-grid">
              <div className="field">
                <label className="field-label">Title</label>
                <input className="input" value={basicFields.title} onChange={(e) => setBasicFields({ ...basicFields, title: e.target.value })} />
              </div>
              <div className="field">
                <label className="field-label">Practice name</label>
                <input className="input" value={basicFields.practiceName} onChange={(e) => setBasicFields({ ...basicFields, practiceName: e.target.value })} />
              </div>
            </div>
            <div className="field">
              <label className="field-label">Languages (one per line or comma)</label>
              <textarea className="input" rows={3} value={basicFields.languages} onChange={(e) => setBasicFields({ ...basicFields, languages: e.target.value })} />
            </div>
            <div className="field-grid">
              <div className="field">
                <label className="field-label">Modes (one per line)</label>
                <textarea className="input" rows={3} value={basicFields.modes} onChange={(e) => setBasicFields({ ...basicFields, modes: e.target.value })} placeholder="TELEHEALTH, IN_PERSON" />
              </div>
              <div className="field">
                <label className="field-label">Availability tags</label>
                <textarea
                  className="input"
                  rows={3}
                  value={basicFields.availabilityTags}
                  onChange={(e) => setBasicFields({ ...basicFields, availabilityTags: e.target.value })}
                  placeholder="UNDER_WEEK, ONE_TO_FOUR_WEEKS"
                />
              </div>
            </div>
            <div className="field-grid">
              <div className="field">
                <label className="field-label">Fee range (text)</label>
                <input className="input" value={basicFields.feeRange} onChange={(e) => setBasicFields({ ...basicFields, feeRange: e.target.value })} placeholder="e.g. 150-250" />
              </div>
              <div className="field">
                <label className="field-label">Pricing min (AUD)</label>
                <input className="input" value={basicFields.pricingMin} onChange={(e) => setBasicFields({ ...basicFields, pricingMin: e.target.value })} inputMode="decimal" />
              </div>
              <div className="field">
                <label className="field-label">Pricing max (AUD)</label>
                <input className="input" value={basicFields.pricingMax} onChange={(e) => setBasicFields({ ...basicFields, pricingMax: e.target.value })} inputMode="decimal" />
              </div>
            </div>
            <div className="field-grid">
              <div className="field">
                <label className="field-label">Registration number</label>
                <input className="input" value={basicFields.registrationNumber} onChange={(e) => setBasicFields({ ...basicFields, registrationNumber: e.target.value })} />
              </div>
              <div className="field">
                <label className="field-label">Registration authority</label>
                <input className="input" value={basicFields.registrationAuthority} onChange={(e) => setBasicFields({ ...basicFields, registrationAuthority: e.target.value })} />
              </div>
            </div>
            <div className="field-grid">
              <div className="field">
                <label className="field-label">ABN</label>
                <input className="input" value={basicFields.abn} onChange={(e) => setBasicFields({ ...basicFields, abn: e.target.value })} />
              </div>
              <div className="field">
                <label className="field-label">Trading name</label>
                <input className="input" value={basicFields.tradingName} onChange={(e) => setBasicFields({ ...basicFields, tradingName: e.target.value })} />
              </div>
            </div>
            <div className="field-grid">
              <div className="field">
                <label className="field-label">City / suburb</label>
                <input className="input" value={basicFields.city} onChange={(e) => setBasicFields({ ...basicFields, city: e.target.value })} />
              </div>
              <div className="field">
                <label className="field-label">Address state</label>
                <input className="input" value={basicFields.state} onChange={(e) => setBasicFields({ ...basicFields, state: e.target.value })} />
              </div>
              <div className="field">
                <label className="field-label">Postal code</label>
                <input className="input" value={basicFields.postalCode} onChange={(e) => setBasicFields({ ...basicFields, postalCode: e.target.value })} />
              </div>
            </div>
            <div className="field-grid">
              <div className="field">
                <label className="field-label">Public email</label>
                <input className="input" value={basicFields.publicEmail} onChange={(e) => setBasicFields({ ...basicFields, publicEmail: e.target.value })} />
              </div>
              <div className="field">
                <label className="field-label">Public phone</label>
                <input className="input" value={basicFields.publicPhone} onChange={(e) => setBasicFields({ ...basicFields, publicPhone: e.target.value })} />
              </div>
            </div>
            <div className="field-grid">
              <div className="field">
                <label className="field-label">Admin email</label>
                <input className="input" value={basicFields.adminEmail} onChange={(e) => setBasicFields({ ...basicFields, adminEmail: e.target.value })} />
              </div>
              <div className="field">
                <label className="field-label">Admin phone</label>
                <input className="input" value={basicFields.adminPhone} onChange={(e) => setBasicFields({ ...basicFields, adminPhone: e.target.value })} />
              </div>
            </div>
            <div className="field">
              <label className="field-label">Admin note (optional)</label>
              <input className="input" value={basicFields.note} onChange={(e) => setBasicFields({ ...basicFields, note: e.target.value })} placeholder="Internal note for this edit" />
            </div>
            <div className="field">
              <label className="field-label">Extra JSON (optional)</label>
              <textarea
                className="input"
                rows={6}
                value={basicFields.extraJson}
                onChange={(e) => setBasicFields({ ...basicFields, extraJson: e.target.value })}
                placeholder='{"pricing":{"minAud":120}}'
              />
              <div className="muted" style={{ marginTop: 4 }}>
                Any keys here are merged into the update payload (only allowed fields are applied on the server).
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <button className="btn btn-primary" type="button" onClick={saveBasic} disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
              {message && <span className="success">{message}</span>}
              {err && (
                <span className="error" role="alert">
                  {err}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 12 }}>
            {message && <div className="success">{message}</div>}
            {err && (
              <div className="error" role="alert">
                {err}
              </div>
            )}
          </div>
        )
      ) : null}

      {viewTab === 'reviews' && (
        <div className="card" style={{ padding: 16, marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div>
            <h3 style={{ margin: '0 0 4px' }}>Reviews (admin)</h3>
            <p className="muted" style={{ margin: 0 }}>
              View, edit, add, or remove provider reviews. Stack: {profession}
            </p>
          </div>
          <button
            className="btn btn-outline"
            type="button"
            onClick={() => loadReviews(true)}
            disabled={reviewsLoading || !currentProfile?.userId || profession === 'app'}
          >
            {reviewsLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {profession === 'app' ? (
          <p className="muted" style={{ marginTop: 12 }}>
            Reviews are not supported on the app stack. Switch to a profession stack to manage reviews.
          </p>
        ) : !currentProfile?.userId ? (
          <p className="muted" style={{ marginTop: 12 }}>Load a profile to manage reviews.</p>
        ) : (
          <>
            {reviewsError && (
              <div className="error" role="alert" style={{ marginTop: 12 }}>
                {reviewsError}
              </div>
            )}

            <div style={{ marginTop: 12, padding: 12, border: '1px dashed #ddd', borderRadius: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <div>
                  <strong>Import reviews (CSV)</strong>
                  <p className="muted" style={{ margin: '4px 0 0' }}>
                    Columns: rating, reviewerName, reviewDateIso, source, comment. Optional: reviewerId, selectionId, clientId.
                  </p>
                </div>
              </div>
              <div style={{ marginTop: 8, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  key={csvImportKey}
                  className="input"
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleCsvFileChange}
                  disabled={csvImportBusy || reviewsLoading || reviewSaving}
                />
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={importCsvReviews}
                  disabled={csvImportBusy || reviewsLoading || reviewSaving || !csvImportRows.length}
                >
                  {csvImportBusy
                    ? 'Importing…'
                    : csvValidCount
                      ? `Import ${csvValidCount} review${csvValidCount === 1 ? '' : 's'}`
                      : 'Import reviews'}
                </button>
              </div>
              {csvImportFileName ? (
                <div className="muted" style={{ marginTop: 6 }}>
                  File: {csvImportFileName}
                </div>
              ) : null}
              {csvImportNotice ? (
                <div className="success" style={{ marginTop: 6 }}>
                  {csvImportNotice}
                </div>
              ) : null}
              {csvImportError ? (
                <div className="error" role="alert" style={{ marginTop: 6 }}>
                  {csvImportError}
                </div>
              ) : null}
              {csvImportRows.length ? (
                <div className="muted" style={{ marginTop: 6 }}>
                  {csvValidCount} ready{csvInvalidCount ? `, ${csvInvalidCount} with issues` : ''}. Showing first {Math.min(3, csvImportRows.length)} rows.
                </div>
              ) : null}
              {csvPreviewRows.length ? (
                <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
                  {csvPreviewRows.map((row) => (
                    <div key={`csv-row-${row.rowNumber}`} className="muted">
                      Row {row.rowNumber}: {row.rating ?? '—'} ★ · {row.reviewerName || 'Unknown'} ·{' '}
                      {row.reviewDateIso || 'No date'} · {row.source || 'No source'}
                      {row.errors?.length ? ` · Issues: ${row.errors.join('; ')}` : ''}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="field-grid" style={{ marginTop: 12 }}>
              <div className="field">
                <label className="field-label">Add review · Rating (1-5)</label>
                <input
                  className="input"
                  value={newReview.rating}
                  inputMode="decimal"
                  onChange={(e) => setNewReview({ ...newReview, rating: e.target.value })}
                  placeholder="5"
                />
              </div>
              <div className="field">
                <label className="field-label">Reviewer ID (optional)</label>
                <input
                  className="input"
                  value={newReview.reviewerId}
                  onChange={(e) => setNewReview({ ...newReview, reviewerId: e.target.value })}
                  placeholder="clientId / userId"
                />
              </div>
              <div className="field">
                <label className="field-label">Reviewer name (optional)</label>
                <input
                  className="input"
                  value={newReview.reviewerFirstName}
                  onChange={(e) => setNewReview({ ...newReview, reviewerFirstName: e.target.value })}
                  placeholder="Admin / Client name"
                />
              </div>
            </div>
            <div className="field-grid">
              <div className="field">
                <label className="field-label">Selection / appointment ID (optional)</label>
                <input
                  className="input"
                  value={newReview.selectionId}
                  onChange={(e) => setNewReview({ ...newReview, selectionId: e.target.value })}
                  placeholder="selectionId"
                />
              </div>
              <div className="field">
                <label className="field-label">Date &amp; time (optional)</label>
                <input
                  className="input"
                  type="datetime-local"
                  value={newReview.slotISO}
                  onChange={(e) => setNewReview({ ...newReview, slotISO: e.target.value })}
                  placeholder="2026-01-13T09:30"
                />
              </div>
              <div className="field">
                <label className="field-label">Client ID (optional)</label>
                <input
                  className="input"
                  value={newReview.patientId}
                  onChange={(e) => setNewReview({ ...newReview, patientId: e.target.value })}
                  placeholder="clientId"
                />
              </div>
              <div className="field">
                <label className="field-label">Source (optional)</label>
                <input
                  className="input"
                  value={newReview.source}
                  onChange={(e) => setNewReview({ ...newReview, source: e.target.value })}
                  placeholder="Google, Facebook, etc."
                />
              </div>
            </div>
            <div className="field">
              <label className="field-label">Comment</label>
              <textarea
                className="input"
                rows={3}
                value={newReview.comment}
                onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                placeholder="Optional review comment"
              />
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
              <button className="btn btn-primary" type="button" onClick={createReview} disabled={reviewSaving || reviewsLoading}>
                {reviewSaving ? 'Saving…' : 'Add review'}
              </button>
              <button
                className="btn btn-outline"
                type="button"
                onClick={() =>
                  setNewReview({
                    rating: '',
                    comment: '',
                    source: '',
                    reviewerId: '',
                    reviewerFirstName: '',
                    selectionId: '',
                    slotISO: '',
                    patientId: '',
                  })
                }
              >
                Clear
              </button>
            </div>

            <div style={{ marginTop: 8 }}>
              {reviewsLoading && reviews.length === 0 ? <Spinner label="Loading reviews…" /> : null}
              {!reviewsLoading && reviews.length === 0 ? <p className="muted">No reviews yet.</p> : null}
              {reviews.map((rev) => (
                <div key={rev.reviewId || rev.id} className="card" style={{ padding: 12, marginTop: 8, border: '1px solid #eee' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <div>
                      <strong>{rev.rating != null ? rev.rating : '–'} ★</strong>{' '}
                      <span className="muted">
                        {rev.reviewerFirstName || rev.reviewerId || 'Unknown reviewer'} · {rev.createdAt || ''}
                        {rev.source ? ` - ${rev.source}` : ''}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {editReviewId === rev.reviewId ? (
                        <>
                          <button className="btn btn-primary" type="button" onClick={saveEditReview} disabled={reviewSaving}>
                            {reviewSaving ? 'Saving…' : 'Save'}
                          </button>
                          <button className="btn btn-outline" type="button" onClick={() => setEditReviewId(null)} disabled={reviewSaving}>
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button className="btn btn-outline" type="button" onClick={() => startEditReview(rev)} disabled={reviewSaving}>
                            Edit
                          </button>
                          <button
                            className="btn btn-outline"
                            type="button"
                            onClick={() => deleteReview(rev.reviewId || rev.id)}
                            disabled={reviewSaving || reviewsLoading}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="muted" style={{ marginTop: 4 }}>
                    Selection: {rev.selectionId || '—'} · Slot: {rev.slotISO || rev.appointmentId || '—'} · Client: {rev.patientId || '—'}
                  </div>
                  {editReviewId === rev.reviewId ? (
                    <>
                    <div className="field-grid" style={{ marginTop: 8 }}>
                      <div className="field">
                        <label className="field-label">Rating</label>
                        <input
                          className="input"
                          value={editReviewFields.rating}
                          inputMode="decimal"
                          onChange={(e) => setEditReviewFields({ ...editReviewFields, rating: e.target.value })}
                        />
                      </div>
                      <div className="field">
                        <label className="field-label">Reviewer ID</label>
                        <input
                          className="input"
                          value={editReviewFields.reviewerId}
                          onChange={(e) => setEditReviewFields({ ...editReviewFields, reviewerId: e.target.value })}
                          placeholder="clientId / userId"
                        />
                      </div>
                      <div className="field">
                        <label className="field-label">Reviewer name</label>
                        <input
                          className="input"
                          value={editReviewFields.reviewerFirstName}
                          onChange={(e) => setEditReviewFields({ ...editReviewFields, reviewerFirstName: e.target.value })}
                          placeholder="Admin / Client name"
                        />
                      </div>
                    </div>
                    <div className="field-grid" style={{ marginTop: 8 }}>
                      <div className="field">
                        <label className="field-label">Selection / appointment ID</label>
                        <input
                          className="input"
                          value={editReviewFields.selectionId}
                          onChange={(e) => setEditReviewFields({ ...editReviewFields, selectionId: e.target.value })}
                          placeholder="selectionId"
                        />
                      </div>
                      <div className="field">
                        <label className="field-label">Date &amp; time</label>
                        <input
                          className="input"
                          type="datetime-local"
                          value={editReviewFields.slotISO}
                          onChange={(e) => setEditReviewFields({ ...editReviewFields, slotISO: e.target.value })}
                          placeholder="2026-01-13T09:30"
                        />
                      </div>
                      <div className="field">
                        <label className="field-label">Client ID</label>
                        <input
                          className="input"
                          value={editReviewFields.patientId}
                          onChange={(e) => setEditReviewFields({ ...editReviewFields, patientId: e.target.value })}
                          placeholder="clientId"
                        />
                      </div>
                    </div>
                    <div className="field-grid" style={{ marginTop: 8 }}>
                      <div className="field" style={{ gridColumn: 'span 2' }}>
                        <label className="field-label">Comment</label>
                        <textarea
                          className="input"
                          rows={3}
                          value={editReviewFields.comment}
                          onChange={(e) => setEditReviewFields({ ...editReviewFields, comment: e.target.value })}
                        />
                      </div>
                      <div className="field">
                        <label className="field-label">Source</label>
                        <input
                          className="input"
                          value={editReviewFields.source}
                          onChange={(e) => setEditReviewFields({ ...editReviewFields, source: e.target.value })}
                          placeholder="Google, Facebook, etc."
                        />
                      </div>
                    </div>
                    </>
                  ) : (
                    <p style={{ marginTop: 8 }}>{rev.comment || <span className="muted">No comment</span>}</p>
                  )}
                </div>
              ))}
              {reviewsCursor && (
                <div style={{ marginTop: 12 }}>
                  <button className="btn btn-outline" type="button" onClick={() => loadReviews(false)} disabled={reviewsLoading}>
                    {reviewsLoading ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      )}

      {viewTab === 'approvals' && (
        <div className="card" style={{ padding: 16, marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div>
              <h3 style={{ margin: '0 0 4px' }}>Approvals</h3>
              <p className="muted" style={{ margin: 0 }}>
                Approve provider profiles across stacks. Enter the provider UID above, then choose stacks below.
              </p>
              <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
                Build: {APPROVALS_BUILD}
              </div>
              <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
                {approvalDebugSummary}
              </div>
            </div>
            <button
              className="btn btn-outline"
              type="button"
              onClick={loadApprovalStatus}
              disabled={approvalLoading}
            >
              {approvalLoading ? 'Checking…' : 'Check status'}
            </button>
          </div>

          <div className="field" style={{ marginTop: 16 }}>
            <label className="field-label">Stacks</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              <button className="btn btn-outline" type="button" onClick={selectAllApprovalStacks} disabled={approvalLoading}>
                Select all
              </button>
              <button className="btn btn-outline" type="button" onClick={clearApprovalStacks} disabled={approvalLoading}>
                Clear
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
              {APPROVAL_STACKS.map((stack) => (
                <label key={stack.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={approvalStacks.includes(stack.id)}
                    onChange={() => toggleApprovalStack(stack.id)}
                  />
                  <span>{stack.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" type="button" onClick={approveSelectedStacks} disabled={approvalLoading}>
              {approvalLoading ? 'Approving…' : 'Approve selected'}
            </button>
            {approvalVisibleMessage && <span className="success">{approvalVisibleMessage}</span>}
            {approvalError && (
              <span className="error" role="alert">
                {approvalError}
              </span>
            )}
          </div>

          {approvalStacks.length > 0 && (
            <div style={{ marginTop: 16, display: 'grid', gap: 12 }}>
              {approvalStacks.map((stackId) => {
                const meta = APPROVAL_STACKS.find((stack) => stack.id === stackId)
                const result = approvalRenderResult(stackId)
                const draftKey = displayLabelDraftKey(stackId)
                const statusLabel = toSafeText(result.status, 'not checked') || 'not checked'
                return (
                  <div key={stackId} className="card" style={{ padding: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                      <div>
                        <strong>{meta?.label || stackId}</strong>
                        <div className="muted" style={{ marginTop: 4 }}>
                          Status: {statusLabel}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-outline"
                          type="button"
                          onClick={async () => {
                            setApprovalError(null)
                            setApprovalMessage(null)
                            setApprovalLoading(true)
                            try {
                              const stackUserId = await resolveApprovalStackUserId(stackId)
                              const res = await adminApproveProvider(stackId, stackUserId)
                              const already = Boolean(res?.already)
                              setApprovalResults((prev) => ({
                                ...prev,
                                [stackId]: {
                                  ...(prev[stackId] || {}),
                                  userId: stackUserId,
                                  status: 'approved',
                                  note: already ? 'Already approved' : undefined,
                                  error: undefined,
                                },
                              }))
                              setApprovalDisplayResults((prev) => ({
                                ...prev,
                                [stackId]: {
                                  ...(prev[stackId] || {}),
                                  userId: stackUserId,
                                  status: 'approved',
                                  note: already ? 'Already approved' : undefined,
                                  error: undefined,
                                },
                              }))
                              setApprovalMessage(
                                approvalComposeMergedMessage(
                                  already ? 'Already approved' : `Approved ${meta?.label || stackId}`,
                                  {
                                    [stackId]: {
                                      userId: stackUserId,
                                      status: 'approved',
                                      note: already ? 'Already approved' : undefined,
                                      error: undefined,
                                    },
                                  },
                                  [stackId]
                                )
                              )
                            } catch (e: any) {
                              setApprovalError(e?.message || 'Approval failed')
                              setApprovalResults((prev) => ({
                                ...prev,
                                [stackId]: { ...(prev[stackId] || {}), error: e?.message || 'Approval failed' },
                              }))
                              setApprovalDisplayResults((prev) => ({
                                ...prev,
                                [stackId]: { ...(prev[stackId] || {}), error: e?.message || 'Approval failed' },
                              }))
                            } finally {
                              setApprovalLoading(false)
                            }
                          }}
                          disabled={approvalLoading}
                        >
                          Approve
                        </button>
                        <button
                          className="btn btn-outline"
                          type="button"
                          onClick={async () => {
                            setApprovalError(null)
                            setApprovalMessage(null)
                            setApprovalLoading(true)
                            try {
                              const stackUserId = await resolveApprovalStackUserId(stackId)
                              const res = await adminUnapproveProvider(stackId, stackUserId)
                              const already = Boolean(res?.already)
                              setApprovalResults((prev) => ({
                                ...prev,
                                [stackId]: {
                                  ...(prev[stackId] || {}),
                                  userId: stackUserId,
                                  status: 'pending',
                                  note: already ? 'Already pending' : undefined,
                                  error: undefined,
                                },
                              }))
                              setApprovalDisplayResults((prev) => ({
                                ...prev,
                                [stackId]: {
                                  ...(prev[stackId] || {}),
                                  userId: stackUserId,
                                  status: 'pending',
                                  note: already ? 'Already pending' : undefined,
                                  error: undefined,
                                },
                              }))
                              setApprovalMessage(
                                approvalComposeMergedMessage(
                                  already ? 'Already pending' : `Set ${meta?.label || stackId} to pending`,
                                  {
                                    [stackId]: {
                                      userId: stackUserId,
                                      status: 'pending',
                                      note: already ? 'Already pending' : undefined,
                                      error: undefined,
                                    },
                                  },
                                  [stackId]
                                )
                              )
                            } catch (e: any) {
                              setApprovalError(e?.message || 'Unapprove failed')
                              setApprovalResults((prev) => ({
                                ...prev,
                                [stackId]: { ...(prev[stackId] || {}), error: e?.message || 'Unapprove failed' },
                              }))
                              setApprovalDisplayResults((prev) => ({
                                ...prev,
                                [stackId]: { ...(prev[stackId] || {}), error: e?.message || 'Unapprove failed' },
                              }))
                            } finally {
                              setApprovalLoading(false)
                            }
                          }}
                          disabled={approvalLoading}
                        >
                          Unapprove
                        </button>
                      </div>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <div className="muted">Name: {toSafeText(result.name, '') || '—'}</div>
                      <div className="muted">Email: {toSafeText(result.email, '') || '—'}</div>
                      <div className="muted">Updated: {toSafeText(result.updatedAt, '') || '—'}</div>
                      <div className="muted">Display label: {toSafeText(result.displayProfession, '') || '—'}</div>
                      {result.note && <div className="muted">Note: {result.note}</div>}
                      {result.error && (
                        <div className="error" role="alert" style={{ marginTop: 6 }}>
                          {result.error}
                        </div>
                      )}
                      <div style={{ marginTop: 10 }}>
                        <label className="field-label">Display label</label>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          <input
                            className="input"
                            value={
                              displayLabelDrafts[draftKey] !== undefined
                                ? displayLabelDrafts[draftKey]
                                : result.displayProfession || ''
                            }
                            onChange={(e) =>
                              setDisplayLabelDrafts((prev) => ({ ...prev, [draftKey]: e.target.value }))
                            }
                            placeholder="e.g. Finance Broker"
                          />
                          <button
                            className="btn btn-outline"
                            type="button"
                            disabled={approvalLoading}
                            onClick={async () => {
                              const label = String(displayLabelDrafts[draftKey] || '').trim()
                              if (!label) {
                                setApprovalError('Enter a display label or clear it')
                                return
                              }
                              setApprovalError(null)
                              setApprovalMessage(null)
                              setApprovalLoading(true)
                              try {
                                const stackUserId = await resolveApprovalStackUserId(stackId)
                                const res = await adminSetProviderDisplay({ userId: stackUserId, stackId, label })
                                const nextLabel = res?.displayProfession || label
                                setApprovalResults((prev) => ({
                                  ...prev,
                                  [stackId]: {
                                    ...(prev[stackId] || {}),
                                    userId: stackUserId,
                                    displayProfession: nextLabel,
                                  },
                                }))
                                setApprovalDisplayResults((prev) => ({
                                  ...prev,
                                  [stackId]: {
                                    ...(prev[stackId] || {}),
                                    userId: stackUserId,
                                    displayProfession: nextLabel,
                                  },
                                }))
                                setDisplayLabelDrafts((prev) => ({ ...prev, [draftKey]: nextLabel }))
                                setApprovalMessage(
                                  approvalComposeMergedMessage(
                                    `Display label updated for ${meta?.label || stackId}`,
                                    {
                                      [stackId]: {
                                        userId: stackUserId,
                                        displayProfession: nextLabel,
                                      },
                                    },
                                    [stackId]
                                  )
                                )
                              } catch (e: any) {
                                setApprovalError(e?.message || 'Failed to update display label')
                              } finally {
                                setApprovalLoading(false)
                              }
                            }}
                          >
                            Save label
                          </button>
                          <button
                            className="btn btn-outline"
                            type="button"
                            disabled={approvalLoading}
                            onClick={async () => {
                              setApprovalError(null)
                              setApprovalMessage(null)
                              setApprovalLoading(true)
                              try {
                                const stackUserId = await resolveApprovalStackUserId(stackId)
                                await adminSetProviderDisplay({ userId: stackUserId, stackId, clear: true })
                                setApprovalResults((prev) => ({
                                  ...prev,
                                  [stackId]: {
                                    ...(prev[stackId] || {}),
                                    userId: stackUserId,
                                    displayProfession: '',
                                  },
                                }))
                                setApprovalDisplayResults((prev) => ({
                                  ...prev,
                                  [stackId]: {
                                    ...(prev[stackId] || {}),
                                    userId: stackUserId,
                                    displayProfession: '',
                                  },
                                }))
                                setDisplayLabelDrafts((prev) => ({ ...prev, [draftKey]: '' }))
                                setApprovalMessage(
                                  approvalComposeMergedMessage(
                                    `Display label cleared for ${meta?.label || stackId}`,
                                    {
                                      [stackId]: {
                                        userId: stackUserId,
                                        displayProfession: '',
                                      },
                                    },
                                    [stackId]
                                  )
                                )
                              } catch (e: any) {
                                setApprovalError(e?.message || 'Failed to clear display label')
                              } finally {
                                setApprovalLoading(false)
                              }
                            }}
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {viewTab === 'users' && (
        <div className="card" style={{ padding: 16, marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div>
              <h3 style={{ margin: '0 0 4px' }}>User list</h3>
              <p className="muted" style={{ margin: 0 }}>
                Matches `user-admin.mjs list --role all --sort created` with quick filters for providers.
              </p>
            </div>
            <div className="inline-gap-2">
              <button
                className="btn btn-outline"
                type="button"
                onClick={handleExportUserList}
                disabled={userListLoading || !userListItems.length}
                title={userListItems.length ? `Export ${userListFilterLabel}` : 'No users to export'}
              >
                Export CSV
              </button>
              <button className="btn btn-outline" type="button" onClick={() => loadUserList()} disabled={userListLoading}>
                {userListLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
          </div>

          <div className="field" style={{ marginTop: 16, maxWidth: 360 }}>
            <label className="field-label">View</label>
            <select
              className="input"
              value={userListFilter}
              onChange={(e) => setUserListFilter(e.target.value as any)}
            >
              {userListOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {userListError && (
            <div className="error" role="alert" style={{ marginTop: 12 }}>
              {userListError}
            </div>
          )}

          <div style={{ marginTop: 12 }} className="muted">
            {userListItems.length ? `${userListItems.length} users` : userListLoading ? 'Loading list…' : 'No users found.'}
          </div>

          {!!userListItems.length && (
            <div style={{ marginTop: 12, borderTop: '1px solid #e5e7eb' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.1fr 1.3fr 1fr 0.9fr 1.2fr',
                  gap: 12,
                  padding: '10px 0',
                  fontWeight: 600,
                }}
              >
                <div>Name</div>
                <div>Email</div>
                <div>Role / Stack</div>
                <div>Status</div>
                <div>Created</div>
              </div>
              {userListItems.map((item, idx) => {
                const scope = item.scope === 'provider' ? 'Provider' : 'Client'
                const stackLabel =
                  item.scope === 'provider'
                    ? item.scopeLabel || item.stackId || '—'
                    : item.scopeLabel || 'Match'
                return (
                  <div
                    key={`${item.userId || 'user'}-${idx}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1.1fr 1.3fr 1fr 0.9fr 1.2fr',
                      gap: 12,
                      padding: '10px 0',
                      borderTop: '1px solid #eef0f3',
                      fontSize: '0.95rem',
                    }}
                  >
                    <div>{item.name || '—'}</div>
                    <div>{item.email || '—'}</div>
                    <div>
                      {scope}
                      {stackLabel ? ` · ${stackLabel}` : ''}
                    </div>
                    <div>{item.status || '—'}</div>
                    <div>{item.createdAt || '—'}</div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {viewTab === 'requests' && (
        <div className="card" style={{ padding: 16, marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div>
              <h3 style={{ margin: '0 0 4px' }}>Client requests</h3>
              <p className="muted" style={{ margin: 0 }}>
                Lists client requests and follow-up responses (Yes/No). Most recent first.
              </p>
            </div>
            <div className="inline-gap-2">
              <button
                className="btn btn-outline"
                type="button"
                onClick={handleExportRequestList}
                disabled={requestLoading || !sortedRequestItems.length}
                title={sortedRequestItems.length ? 'Export visible requests' : 'No requests to export'}
              >
                Export CSV
              </button>
              <button
                className="btn btn-outline"
                type="button"
                onClick={() => loadRequestList(true)}
                disabled={requestLoading}
              >
                {requestLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
          </div>

          <div className="field" style={{ marginTop: 16, maxWidth: 360 }}>
            <label className="field-label">Stack</label>
            <select
              className="input"
              value={requestStack}
              onChange={(e) => setRequestStack(e.target.value as RequestStackFilter)}
            >
              <option value="all">All stacks</option>
              {APPROVAL_STACKS.map((stack) => (
                <option key={stack.id} value={stack.id}>
                  {stack.label}
                </option>
              ))}
            </select>
          </div>
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 10,
              fontSize: '0.9rem',
            }}
          >
            <input
              type="checkbox"
              checked={showFilteredRequests}
              onChange={(e) => setShowFilteredRequests(e.target.checked)}
            />
            Show filtered requests (include internal/test emails)
          </label>

          {requestError && (
            <div className="error" role="alert" style={{ marginTop: 12 }}>
              {requestError}
            </div>
          )}

          <div style={{ marginTop: 12 }} className="muted">
            {sortedRequestItems.length
              ? `${sortedRequestItems.length} requests`
              : requestLoading
              ? 'Loading requests…'
              : 'No requests found.'}
          </div>

          {!!sortedRequestItems.length && (
            <div style={{ marginTop: 12, borderTop: '1px solid #e5e7eb' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1.1fr 1fr 1.3fr 1.1fr 1fr 1fr 1fr',
                  gap: 12,
                  padding: '10px 0',
                  fontWeight: 600,
                }}
                >
                  <div>Submitted</div>
                  <div>Stack</div>
                  <div>Client</div>
                  <div>Professional requested</div>
                  <div>Round 1</div>
                  <div>Round 2</div>
                  <div>Last response</div>
                </div>
              {sortedRequestItems.map((item, idx) => (
                <div
                  key={`${item.stackId || 'stack'}-${item.selectionId || 'request'}-${idx}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1.1fr 1fr 1.3fr 1.1fr 1fr 1fr 1fr',
                    gap: 12,
                    padding: '10px 0',
                    borderTop: '1px solid #eef0f3',
                    fontSize: '0.95rem',
                  }}
                >
                  <div>
                    <div>{formatRequestTime(item.createdAt)}</div>
                    <div className="muted" style={{ fontSize: '0.85rem' }}>{item.selectionId || '—'}</div>
                  </div>
                  <div>{item.stackLabel || item.stackId || '—'}</div>
                  <div>
                    <div>{item.client?.name || '—'}</div>
                    <div className="muted" style={{ fontSize: '0.85rem' }}>
                      {item.client?.email || '—'}
                    </div>
                  </div>
                  <div title={getRequestedProfessionalNames(item).join(', ') || undefined}>
                    {getRequestedProfessionalDisplay(item)}
                  </div>
                  <div>{formatResponseWithTime(item.followupRound1Response, item.followupRound1At)}</div>
                  <div>{formatResponseWithTime(item.followupRound2Response, item.followupRound2At)}</div>
                  <div>{formatResponseWithTime(item.followupLastResponse, item.followupLastAt)}</div>
                </div>
              ))}
            </div>
          )}

          {requestCursor && (
            <div style={{ marginTop: 12 }}>
              <button
                className="btn btn-outline"
                type="button"
                onClick={() => loadRequestList(false)}
                disabled={requestLoading}
              >
                {requestLoading ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      )}

      {viewTab === 'stats' && (
        <div className="card" style={{ padding: 16, marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div>
              <h3 style={{ margin: '0 0 4px' }}>Provider stats</h3>
              <p className="muted" style={{ margin: 0 }}>
                Totals with last 30/90 day activity. Use stack to narrow the list.
              </p>
            </div>
            <button className="btn btn-outline" type="button" onClick={() => loadStats(true)} disabled={statsLoading}>
              {statsLoading ? 'Loading…' : 'Refresh'}
            </button>
          </div>

          <div className="field" style={{ marginTop: 16, maxWidth: 360 }}>
            <label className="field-label">Stack</label>
            <select className="input" value={statsStack} onChange={(e) => setStatsStack(e.target.value as ProviderProfession)}>
              {APPROVAL_STACKS.map((stack) => (
                <option key={stack.id} value={stack.id}>
                  {stack.label}
                </option>
              ))}
            </select>
          </div>

          {statsError && (
            <div className="error" role="alert" style={{ marginTop: 12 }}>
              {statsError}
            </div>
          )}

          <div style={{ marginTop: 12 }} className="muted">
            {statsItems.length ? `${statsItems.length} providers` : statsLoading ? 'Loading stats…' : 'No providers found.'}
          </div>

          {!!statsItems.length && (
            <div style={{ marginTop: 12, borderTop: '1px solid #e5e7eb', overflowX: 'auto' }}>
              <div style={{ minWidth: 1500 }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns:
                      '1.4fr 0.9fr 1.2fr 1.2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1.2fr 1fr',
                    gap: 12,
                    padding: '10px 0',
                    fontWeight: 600,
                  }}
                >
                  <div>Name</div>
                  <div>Stack</div>
                  <div>Last login</div>
                  <div>Availability set</div>
                  <div>Google sync</div>
                  <div>Calendly sync</div>
                  <div>Zoom links</div>
                  <div>Google Meet links</div>
                  <div>Reviews</div>
                  <div>Review reminders</div>
                  <div>Notes saved</div>
                  <div>Notes published</div>
                  <div>Last accepted</div>
                  <div>Accepted total</div>
                </div>
                {statsItems.map((row, idx) => {
                  const stackLabel = APPROVAL_STACKS.find((stack) => stack.id === row.stack)?.label || row.stack || '—'
                  return (
                    <div
                      key={`${row.userId || 'provider'}-${idx}`}
                      style={{
                        display: 'grid',
                        gridTemplateColumns:
                          '1.4fr 0.9fr 1.2fr 1.2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1.2fr 1fr',
                        gap: 12,
                        padding: '12px 0',
                        borderTop: '1px solid #eef0f3',
                        fontSize: '0.95rem',
                      }}
                    >
                      <div>
                        <div>{row.name || '—'}</div>
                        <div className="muted" style={{ fontSize: '0.75rem' }}>
                          {row.email || '—'}
                        </div>
                      </div>
                      <div>{stackLabel}</div>
                      <div>{formatStatDate(row.lastLoginAt)}</div>
                      <div>{formatStatDate(row.lastAvailabilityAt)}</div>
                      <div>
                        <div>{row.google?.connected ? 'Connected' : '—'}</div>
                        {row.google?.lastSyncAt ? (
                          <div className="muted" style={{ fontSize: '0.75rem' }}>
                            {formatStatDate(row.google.lastSyncAt)}
                          </div>
                        ) : null}
                      </div>
                      <div>
                        <div>{row.calendly?.connected ? 'Connected' : '—'}</div>
                        {row.calendly?.lastSyncAt ? (
                          <div className="muted" style={{ fontSize: '0.75rem' }}>
                            {formatStatDate(row.calendly.lastSyncAt)}
                          </div>
                        ) : null}
                      </div>
                      <div>{renderCountCell(row, 'zoomMeetingsSent')}</div>
                      <div>{renderCountCell(row, 'googleMeetingsSent')}</div>
                      <div>{renderCountCell(row, 'reviewsReceived')}</div>
                      <div>{renderCountCell(row, 'reviewRemindersSent')}</div>
                      <div>{renderCountCell(row, 'notesSaved')}</div>
                      <div>{renderCountCell(row, 'notesPublished')}</div>
                      <div>{formatStatDate(row.lastAcceptanceAt)}</div>
                      <div>{renderCountCell(row, 'requestsAccepted')}</div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {statsCursor && (
            <div style={{ marginTop: 16 }}>
              <button className="btn btn-outline" type="button" onClick={() => loadStats(false)} disabled={statsLoading}>
                {statsLoading ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      )}

      {viewTab === 'emails' && (
        <div className="card" style={{ padding: 16, marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div>
              <h3 style={{ margin: '0 0 4px' }}>Emails</h3>
              <p className="muted" style={{ margin: 0 }}>
                Edit stack email templates with validation, preview, and rollback support.
              </p>
            </div>
            <button
              className="btn btn-outline"
              type="button"
              onClick={() => loadEmailTemplates(emailStack)}
              disabled={emailLoading || emailSaving}
            >
              {emailLoading ? 'Loading…' : 'Refresh'}
            </button>
          </div>

          {emailError && (
            <div className="error" role="alert" style={{ marginTop: 12 }}>
              {emailError}
            </div>
          )}
          {emailMessage && (
            <div className="success" style={{ marginTop: 12 }}>
              {emailMessage}
            </div>
          )}

          {emailLoading && !emailTemplates.length ? (
            <div style={{ marginTop: 16 }}>
              <Spinner label="Loading email templates…" />
            </div>
          ) : !emailTemplates.length ? (
            <p className="muted" style={{ marginTop: 16 }}>No email templates found.</p>
          ) : (
            <div style={{ marginTop: 16 }}>
              <div
                style={{
                  display: 'grid',
                  gap: 12,
                  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                }}
              >
                <div className="field">
                  <label className="field-label">Stack</label>
                  <select
                    className="input"
                    value={emailStack}
                    onChange={(e) => setEmailStack(e.target.value as EmailTemplateScope)}
                  >
                    {EMAIL_TEMPLATE_SCOPES.map((stack) => (
                      <option key={stack.id} value={stack.id}>
                        {stack.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label className="field-label">Email template family</label>
                  <select
                    className="input"
                    value={emailTemplateId}
                    onChange={(e) => selectEmailTemplate(e.target.value)}
                  >
                    {emailFamilyOptions.length ? (
                      emailFamilyOptions.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}{item.isShared ? ' · Shared' : ' · Stack specific'}
                        </option>
                      ))
                    ) : (
                      <option value="">{noVisibleEmailTemplatesMessage}</option>
                    )}
                  </select>
                </div>

                <div className="field">
                  <label className="field-label">Visibility</label>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      minHeight: 42,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={showDeletedEmailTemplates}
                      onChange={(e) => setShowDeletedEmailTemplates(e.target.checked)}
                    />
                    <span style={{ fontSize: '0.92rem' }}>
                      Show deleted templates{deletedEmailTemplateCount ? ` (${deletedEmailTemplateCount})` : ''}
                    </span>
                  </label>
                </div>
              </div>

              {activeEmailTemplate ? (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 12, marginTop: 12 }}>
                  <div style={{ marginBottom: 10 }}>
                    <h4 style={{ margin: 0 }}>{activeEmailTemplate.title || activeEmailTemplate.template}</h4>
                    {activeEmailTemplate.description ? (
                      <p className="muted" style={{ margin: '4px 0 0' }}>
                        {activeEmailTemplate.description}
                      </p>
                    ) : null}
                    <p className="muted" style={{ margin: '8px 0 0', fontSize: '0.82rem' }}>
                      {activeEmailTemplate.isDeleted ? 'Deleted on this stack' : activeEmailTemplate.isCustomized ? 'Customized template' : 'Using default template'} · Key:{' '}
                      <code>{activeEmailTemplate.template}</code>
                      {activeEmailTemplate.updatedAt ? ` · Updated ${activeEmailTemplate.updatedAt}` : ''}
                      {activeEmailTemplate.updatedBy ? ` by ${activeEmailTemplate.updatedBy}` : ''}
                    </p>
                    {!!activeEmailTemplate.unknownVariables?.length && (
                      <p className="muted" style={{ margin: '6px 0 0', color: '#b45309', fontSize: '0.82rem' }}>
                        Unknown placeholders on this stack: {activeEmailTemplate.unknownVariables.join(', ')}
                      </p>
                    )}
                  </div>

                  <div className="field">
                    <label className="field-label">Subject</label>
                    <input
                      className="input"
                      value={emailSubjectDraft}
                      onChange={(e) => setEmailSubjectDraft(e.target.value)}
                      placeholder="Email subject"
                    />
                  </div>

                  <div className="field">
                    <label className="field-label">Email body</label>
                    <NotesRichEditor
                      value={emailHtmlDraft}
                      onChange={setEmailHtmlDraft}
                      placeholder="Write email body..."
                      showUploadActions={false}
                      onImageUpload={handleEmailTemplateImageUpload}
                      onAttachmentUpload={handleEmailTemplateAttachmentUpload}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
                    <button
                      className="btn btn-outline"
                      type="button"
                      onClick={() => runEmailPreview(false)}
                      disabled={emailPreviewing || emailSaving}
                    >
                      {emailPreviewing ? 'Previewing…' : 'Preview this stack'}
                    </button>
                    <button
                      className="btn btn-outline"
                      type="button"
                      onClick={() => runEmailPreview(true)}
                      disabled={emailPreviewing || emailSaving}
                    >
                      {emailPreviewing ? 'Previewing…' : 'Preview all stacks'}
                    </button>
                  </div>

                  {!!activeEmailTemplate.variables?.length && (
                    <div className="field">
                      <label className="field-label">Variables</label>
                      <p className="muted" style={{ margin: '0 0 8px' }}>
                        Use these placeholders in subject or body.
                      </p>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {activeEmailTemplate.variables.map((variable) => (
                          <code
                            key={`${activeEmailTemplate.template}-${variable}`}
                            style={{
                              background: '#f5f7fb',
                              border: '1px solid #d9dee7',
                              borderRadius: 8,
                              padding: '4px 8px',
                              fontSize: '0.82rem',
                            }}
                          >
                            {`{{${variable}}}`}
                          </code>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={saveEmailTemplate}
                      disabled={emailSaving || !emailDraftDirty}
                    >
                      {emailSaving ? 'Saving…' : `Save this scope (${emailStack})`}
                    </button>
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={saveEmailTemplateAllStacks}
                      disabled={emailSaving || emailStack === 'app' || !SHARED_EMAIL_TEMPLATE_SET.has(emailTemplateId)}
                    >
                      {emailSaving ? 'Applying…' : 'Apply to all 8 stacks'}
                    </button>
                    <button
                      className="btn btn-outline"
                      type="button"
                      onClick={resetEmailTemplate}
                      disabled={emailSaving}
                    >
                      Reset to default
                    </button>
                    <button
                      className="btn btn-outline"
                      type="button"
                      onClick={() => selectEmailTemplate(activeEmailTemplate.template)}
                      disabled={emailSaving}
                    >
                      Revert unsaved
                    </button>
                    <button
                      className="btn btn-outline"
                      type="button"
                      onClick={() => {
                        if (!activeEmailTemplate) return
                        selectEmailTemplate(activeEmailTemplate.template)
                      }}
                      disabled={emailSaving}
                    >
                      Reload from stack
                    </button>
                    <button
                      className="btn btn-outline"
                      type="button"
                      onClick={deleteEmailTemplate}
                      disabled={emailSaving}
                      style={{ borderColor: '#fecaca', color: '#b91c1c' }}
                    >
                      Delete template
                    </button>
                  </div>

                  {!!activeEmailVersions.length && (
                    <div className="field" style={{ marginTop: 14 }}>
                      <label className="field-label">Version history</label>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                        <select
                          className="input"
                          value={emailRollbackVersionId}
                          onChange={(e) => setEmailRollbackVersionId(e.target.value)}
                          style={{ minWidth: 320 }}
                        >
                          <option value="">Select version to rollback</option>
                          {activeEmailVersions.map((version) => (
                            <option key={version.versionId} value={version.versionId}>
                              {version.updatedAt || version.versionId}
                              {version.updatedBy ? ` · ${version.updatedBy}` : ''}
                              {version.note ? ` · ${version.note}` : ''}
                              {version.deleted ? ' · deleted' : ''}
                            </option>
                          ))}
                        </select>
                        <button
                          className="btn btn-outline"
                          type="button"
                          onClick={rollbackEmailTemplate}
                          disabled={emailSaving || !emailRollbackVersionId}
                        >
                          Rollback selected version
                        </button>
                      </div>
                    </div>
                  )}

                  {!!emailPreviewRows.length && (
                    <div style={{ marginTop: 16, borderTop: '1px solid #eef0f3', paddingTop: 12 }}>
                      <label className="field-label">Preview results</label>
                      <div
                        style={{
                          display: 'grid',
                          gap: 10,
                          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                        }}
                      >
                        {emailPreviewRows.map((row) => (
                          <div
                            key={`${row.stack}-${row.subjectPreview.slice(0, 24)}`}
                            style={{
                              border: '1px solid #e5e7eb',
                              borderRadius: 10,
                              padding: 10,
                              background: row.error || row.unknownVariables.length ? '#fff7ed' : '#f8fafc',
                            }}
                          >
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>{row.stack}</div>
                            {row.error ? (
                              <div className="muted" style={{ color: '#b91c1c', fontSize: '0.82rem' }}>{row.error}</div>
                            ) : (
                              <>
                                <div className="muted" style={{ fontSize: '0.82rem' }}>
                                  Unknown: {row.unknownVariables.length} · Missing: {row.missingVariables.length}
                                </div>
                                <div className="muted" style={{ fontSize: '0.82rem', marginTop: 4 }}>
                                  {row.subjectPreview || 'No subject preview'}
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: 16 }}>
                    <label className="field-label">Preview</label>
                    <p className="muted" style={{ margin: '0 0 8px', fontSize: '0.82rem' }}>
                      Live wrapped preview using sample placeholder values.
                    </p>
                    <div
                      style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 12,
                        padding: 12,
                        background: '#fff',
                        minHeight: 120,
                      }}
                      dangerouslySetInnerHTML={{ __html: emailLivePreviewHtml || emailHtmlDraft || '<p>—</p>' }}
                    />
                  </div>
                </div>
              ) : (
                <p className="muted">
                  {emailFamilyOptions.length ? 'Select a template to edit.' : noVisibleEmailTemplatesMessage}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {viewTab === 'provision' && (
        <div className="card" style={{ padding: 16, marginTop: 16 }}>
          <div>
            <h3 style={{ margin: '0 0 4px' }}>Provision provider</h3>
            <p className="muted" style={{ margin: 0 }}>
              Creates (or reuses) a Firebase user and submits a minimal stack registration with placeholders.
            </p>
          </div>

          <div className="field-grid" style={{ marginTop: 16 }}>
            <div className="field">
              <label className="field-label">Email</label>
              <input
                className="input"
                value={provisionEmail}
                onChange={(e) => setProvisionEmail(e.target.value)}
                placeholder="provider@example.com"
              />
            </div>
            <div className="field">
              <label className="field-label">First name</label>
              <input
                className="input"
                value={provisionFirstName}
                onChange={(e) => setProvisionFirstName(e.target.value)}
                placeholder="First name"
              />
            </div>
            <div className="field">
              <label className="field-label">Last name</label>
              <input
                className="input"
                value={provisionLastName}
                onChange={(e) => setProvisionLastName(e.target.value)}
                placeholder="Last name"
              />
            </div>
            <div className="field">
              <label className="field-label">Stack</label>
              <select
                className="input"
                value={provisionStack}
                onChange={(e) => setProvisionStack(e.target.value as ProviderProfession)}
              >
                {APPROVAL_STACKS.map((stack) => (
                  <option key={stack.id} value={stack.id}>
                    {stack.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
            <button className="btn btn-primary" type="button" onClick={submitProvision} disabled={provisionLoading}>
              {provisionLoading ? 'Provisioning…' : 'Provision provider'}
            </button>
            {provisionMessage && <span className="success">{provisionMessage}</span>}
            {provisionError && (
              <span className="error" role="alert">
                {provisionError}
              </span>
            )}
          </div>
        </div>
      )}
        </>
      )}
    </div>
  )
}
